import { AppConfig } from './models/configuration/AppConfig';
import { ModuleConfig } from './models/configuration/ModuleConfig';
import { DocumentModuleConfig } from './models/configuration/DocumentModuleConfig';
import { GlobalStorageConfigSource } from './models/configuration/global/GlobalStorageConfigSource';
import { DisclaimerDisplayType } from './models/configuration/disclaimers/DisclaimerDisplayType';
import { HeaderComponent } from './models/configuration/header/HeaderLayout';

export const developmentAppConfig: AppConfig = {
    components: {
        fundTypeProperty: 'fund_code',
        showFundTypes: true,
        applicationName: 'KurtosysTest',
        app: {
            applicationId: 'my-fund-center',
            listenFor: [
                {
                    appId: 'ksys-pagination',
                    eventTypes: [
                        {
                            name: 'ksys-on-pagination-change',
                            requiredForInitialLoad: false,
                        },
                    ],
                },
            ],
        },
        globalConfig: {
            analytics: {
                type: 'gtm',
            },
            feedback: {
                feedbackMode: 'toaster',
            },
            watchlist: {
                activeOnLoad: false,
                source: GlobalStorageConfigSource.localStorage,
            },
            searchMillisecondTimeout: 500,
            autoSearchMinChar: 3,
            sourceCulture: 'en-GB',
            searchLimit: 10,
            paging: {
                mode: 'paginationApp',
                identifier: 'ksys-pagination',
            },
            disabledEntityFlag: {
                conditional: {
                    conditions: [
                        {
                            field: 'activeStatus',
                            operator: '=',
                            value: 'inactive',
                        },
                    ],
                },
                queries: [
                    {
                        queryId: 'activeStatus',
                        queryOptionsType: 'property',
                        entityType: 'CLSS',
                        code: 'active_status',
                    },
                ],
            },
            useHtmlTranslations: true,
            eventFlags: {
                onFundTypeChange: {
                    clearFilters: false,
                    selectedModuleMode: 'SwitchToDefault',
                },
            },
            accessibility: {
                landmarks: {
                    main: 'Content',
                    search: 'Search',
                    filters: 'Filters',
                    modules: 'Modules',
                    fundTypes: 'Fund Types',
                },
                charts: {
                    default: {
                        selectedChartPeriod: `the '{selectedPeriod}' toggle is currently selected, starting on the {selectedStartDate} and ending the {selectedEndDate}`,
                        periodDates: `for the period starting on the {startDate} and ending on the {endDate}`,
                        summary: `{title} data with {numberOfDataPoints} records`,
                    },
                    annualisedPerformance: {
                        title: 'Annualised performance bar chart',
                    },
                    cumulativePerformance: {
                        title: 'Fund cumulative performance line chart',
                        description: `Shows the aggregate performance from a fund from launch over various time periods from launch, such as 10 years and 1 month, and represents the portfolio\'s overall long-term performance`,
                        summary: `{title} data for the {period} period with {numberOfDataPoints} records, starting the {startDate} and ending the {endDate}`,
                    },
                    cumulativePerformance2: {
                        title: 'Benchmark cumulative performance line chart',
                    },
                },
                buttons: {
                    DateSelectorFilterClearButton:
                        'Date selector filter clear button',
                    ClearFiltersButton: 'Clear filters button',
                    DropdownButton: 'Dropdown button',
                    ColumnDropdownButton: 'Select a column group',
                    ExplorerDownloadButton: 'Explorer download button',
                    ClearIndividualFiltersButton:
                        'Clear individual filters button',
                    FilterDropdownButton: 'Filter dropdown button',
                    ClearSearch: 'Clear search',
                    FiltersToggleButton: 'Filters toggle button',
                    FundManagersNavButtonPrevious:
                        'Fund managers navigate previous button',
                    FundManagersNavButtonNext:
                        'Fund managers navigate next button',
                    FundManagersNavButton: 'Fund managers navigate button',
                    LoadMoreButton: 'Load more button',
                    ReadMoreLessButton: 'Read more less toggle button',
                    ShareableLinkButton: 'Shareable link button',
                    ShareclassCardExpandButton:
                        'Share class card expand button',
                    ShareclassRowExpander: 'Share class row expander button',
                    SortSelectorDropdownButton: 'Sort selector dropdown button',
                    SortToggleButton: 'Sort toggle button',
                    TooltipIconButton: 'Tool tip icon button',
                    WatchlistButton: 'Watchlist button',
                    WatchlistToggleButton: 'Watchlist toggle button',
                    DocumentTitleButton: 'Document title button',
                    DownloadButtonIcon: 'Download button icon',
                    PreviewButtonIcon: 'Preview button icon',
                    ToastCloseButton: 'Close Notification',
                },
                icons: {
                    ChangeDownIcon: 'Change down icon',
                    ChangeUpIcon: 'Change up icon',
                    CheckboxIcon: 'Checkbox icon',
                    CheckboxMarkedIcon: 'Checkbox marked icon',
                    ChevronRightIcon: 'Chevron right icon',
                    ClearFiltersIcon: 'Clear filters icon',
                    CloseIcon: 'Close icon',
                    CollapseIcon: 'Collapse icon',
                    ColumnDropdownChevronIcon: 'Column dropdown chevron icon',
                    ColumnDropdownChevronIconExpanded:
                        'Column dropdown chevron icon expanded',
                    ColumnSortAscIcon: 'Column sort asc icon',
                    ColumnSortDescIcon: 'Column sort desc icon',
                    ColumnSortOffIcon: 'Column sort off icon',
                    DocumentDropdownIcon: 'Document dropdown icon',
                    DocumentDropdownIconExpanded:
                        'Document dropdown icon expanded',
                    DocumentIcon: 'Document icon',
                    DownloadIcon: 'Download icon',
                    DropdownChevronIcon: 'Dropdown chevron icon',
                    DropdownChevronIconExpanded:
                        'Dropdown chevron icon expanded',
                    ExpandIcon: 'Expand icon',
                    ExplorerDownloadIcon: 'Explorer download icon',
                    FilterDropdownIcon: 'Filter dropdown icon',
                    FilterDropdownIconExpanded: 'Filter dropdown icon expanded',
                    FilterIcon: 'Filter icon',
                    FilterItemHeaderIcon: 'Filter item header icon',
                    FilterItemHeaderIconExpanded:
                        'Filter item header icon expanded',
                    FilterRemoveIcon: 'Filter remove icon',
                    FiltersHeaderToggleIcon: 'Filters header toggle icon',
                    FiltersTopHeaderToggleIcon:
                        'Filters top header toggle icon',
                    FiltersTopHeaderToggleIconExpanded:
                        'Filters top header toggle icon expanded',
                    FundFactChangeDownIcon: 'Fund fact change down icon',
                    FundFactChangeUpIcon: 'Fund fact change up icon',
                    FundManagerPlaceholder: 'Fund manager placeholder',
                    FundManagersNavArrowLeftIcon:
                        'Fund managers nav arrow left icon',
                    FundManagersNavArrowRightIcon:
                        'Fund managers nav arrow right icon',
                    HeaderChevronIconCollapse: 'Header chevron icon collapse',
                    HeaderChevronIconExpand: 'Header chevron icon expand',
                    HeaderDownloadIcon: 'Header download icon',
                    InformationIcon: 'Information icon',
                    MenuIcon: 'Menu icon',
                    MobileCollapseIcon: 'Mobile collapse icon',
                    MobileExpandIcon: 'Mobile expand icon',
                    ModuleDropdownChevronIcon: 'Module dropdown chevron icon',
                    ModuleDropdownChevronIconExpanded:
                        'Module dropdown chevron icon expanded',
                    MorningstarIcon: 'Morningstar icon',
                    MorningstarOutlineIcon: 'Morningstar outline icon',
                    NoChangeIcon: 'No change icon',
                    ReadLessIcon: 'Read less icon',
                    ReadMoreIcon: 'Read more icon',
                    SearchIcon: 'Search icon',
                    SortSelectorDropdownChevronIcon:
                        'Sort selector dropdown chevron icon',
                    SortSelectorDropdownChevronIconExpanded:
                        'Sort selector dropdown chevron icon expanded',
                    WatchlistButtonIcon: 'Watchlist button icon',
                    WatchlistButtonOutlineIcon: 'Watchlist button outline icon',
                    WatchlistToggleIcon: 'Watchlist toggle icon',
                    WatchlistToggleOutlineIcon: 'Watchlist toggle outline icon',
                },
                rowExpander: {
                    columnHeader: 'Expand Column',
                },
                search: {
                    showLabel: false,
                    label: 'Search:',
                },
                links: {
                    fundCardHeaderLink:
                        'Will open a new tab detailing the share class',
                    fundManagerLink:
                        'Will redirect you to a page with more information on the fund manager',
                    keyDocumentsLink:
                        'Will open a new tab with all key documents',
                    shareClassRowLinks:
                        'Will open a new tab that details {label} {value}',
                },
                fact: {
                    fact: 'Fund Fact Item',
                    srri: 'Fund Fact Risk Index',
                    stars: 'Fund Fact Stars',
                    starsOutOf: 'Star Rating Out Of 5 Fund Fact',
                },
                superscripts: {
                    footnoteGroupDescription: 'Footnote disclaimers',
                    footnoteReturnLink:
                        'Return to actioned superscript or first occurrence',
                    footnoteSuperscript:
                        'related superscript character {superscript}',
                    superscriptLink:
                        'Navigate to footnote superscript disclaimer {superscript}',
                },
            },
            valueAliases: {
                cultures: {
                    'en-GB': 'English',
                    'fr-FR': 'French',
                    'de-DE': 'German',
                    'it-IT': 'Italian',
                },
            },
            formats: {
                date: {
                    type: 'date',
                    options: {
                        year: '2-digit',
                        month: '2-digit',
                        day: '2-digit',
                    },
                    optionsByCulture: {
                        'de-DE': {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                        },
                        'zh-HK': {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                        },
                    },
                },
                number: {
                    type: 'number',
                    options: {
                        style: 'decimal',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    },
                    optionsByCulture: {
                        'de-DE': {
                            style: 'decimal',
                            minimumFractionDigits: 3,
                            maximumFractionDigits: 3,
                        },
                    },
                },
            },
            documentPreview: {
                enabled: false,
            },
            disclaimerTypes: [
                'Fees',
                'Past Perf',
                'Fund Card Disabled',
                'No Documents',
            ],
            disclaimersByDocumentType: {
                Factsheet: {
                    disclaimersByCulture: {
                        'de-DE': {
                            conditionalDisclaimers: [
                                {
                                    disclaimerType: 'Fees',
                                    display: DisclaimerDisplayType.tooltip,
                                },
                            ],
                        },
                    },
                },
            },
            extendedInfo: {
                iconPosition: 'right',
                options: {
                    globalInfo: {
                        contentQuery: {
                            queryOptionsType: 'none',
                            value:
                                'This is a <b>GLOBAL CONFIG</b> of the extended Info',
                        },
                        iconFilename: 'info.png',
                    },
                },
            },
            userFilters: {
                source: GlobalStorageConfigSource.localStorage,
            },
            documentEntityKeys: {
                fund: {
                    conditional: {
                        conditions: [
                            {
                                field: 'properties_pub.isin',
                                operator: 'undefined',
                            },
                        ],
                    },
                },
                class: {
                    conditional: {
                        conditions: [
                            {
                                field: 'properties_pub.isin',
                                operator: 'defined',
                            },
                        ],
                    },
                },
            },
        },
        fundTypes: [
            {
                fundType: 'ALPHA',
                label: 'Alpha',
                showModules: true,
                moduleMobileSelector: 'dropdown',
                disclaimers: [
                    {
                        disclaimerType: 'Fees',
                        display: DisclaimerDisplayType.superscript,
                    },
                    {
                        disclaimerType: 'Past Perf',
                        display: DisclaimerDisplayType.superscript,
                    },
                ],
                watchlist: {
                    scope: 'unique',
                    key: 'alpha',
                },
                extendedInfo: {
                    iconPosition: 'right',
                    options: {
                        fundConfig: {
                            contentQuery: {
                                queryOptionsType: 'none',
                                value:
                                    'This is a <b>FUND TYPE CONFIG</b> of the extended Info',
                            },
                            iconFilename: 'warning.png',
                            disabledIconFilename: 'disabled_warning.png',
                        },
                    },
                },
                modules: [
                    {
                        id: 'performance',
                        label: 'Performance',
                        selected: true,
                        disclaimers: [
                            {
                                disclaimerType: 'Fees',
                                display: DisclaimerDisplayType.superscript,
                            },
                            {
                                disclaimerType: 'Past Perf',
                                display: DisclaimerDisplayType.superscript,
                            },
                        ],
                        config: {
                            fundGroupHeader: {
                                fundGroupProperty: 'fund_name',
                                fundSubGroupProperty: 'fund_sub_group',
                                watchlist: {
                                    fundGroup: {
                                        enabled: true,
                                    },
                                    fundSubGroup: {
                                        enabled: true,
                                    },
                                },
                                nonGroupHeaderText: 'Unknown',
                                stickyOffset: 50,
                                expandable: true,
                                expander: {
                                    showIcon: true,
                                    align: 'right',
                                },
                                tile: {
                                    property: 'fund_code',
                                    align: 'left',
                                    propertyFilenameMap: {
                                        '': 'tile-default.svg',
                                        ALPHA: 'equity.svg',
                                        BOND: 'fixed-income.svg',
                                        EQUITY: 'alternatives.svg',
                                        MACRO: 'multi-asset.svg',
                                    },
                                },
                                additionalElements: [
                                    {
                                        label: {
                                            queryOptionsType: 'none',
                                            value: 'Launch Date: ',
                                        },
                                        value: {
                                            queryOptionsType: 'property',
                                            code: 'fund_launch_date',
                                            entityType: 'CLSS',
                                        },
                                    },
                                    {
                                        label: {
                                            queryOptionsType: 'none',
                                            value: 'Fund Nick Name: ',
                                        },
                                        value: {
                                            queryOptionsType: 'property',
                                            code: 'fund_nick_name',
                                            entityType: 'CLSS',
                                        },
                                    },
                                ],
                                subHeaderAdditionalElements: [
                                    {
                                        value: {
                                            queryOptionsType: 'none',
                                            value: 'Demo',
                                        },
                                    },
                                ],
                                sort: [
                                    {
                                        key: 'fund_name',
                                        direction: 'ASC',
                                    },
                                    {
                                        key: 'fund_sub_group',
                                        direction: 'ASC',
                                    },
                                    {
                                        key: 'fund_class_very_short_name',
                                        direction: 'ASC',
                                    },
                                    {
                                        key: 'fund_type_code',
                                        direction: 'ASC',
                                    },
                                ],
                            },
                            responsiveConfig: {
                                layout: {
                                    header: {
                                        sm: [
                                            {
                                                type: HeaderComponent.SEARCH,
                                            },
                                            {
                                                type: HeaderComponent.DOWNLOAD,
                                            },
                                            {
                                                type: HeaderComponent.WATCHLIST,
                                            },
                                            {
                                                type:
                                                    HeaderComponent.TOGGLE_FILTERS,
                                                options: {
                                                    label: 'Filters',
                                                    icon: {
                                                        filename: 'MenuIcon',
                                                    },
                                                    showCount: true,
                                                },
                                            },
                                        ],
                                        md: [
                                            {
                                                type: HeaderComponent.SEARCH,
                                            },
                                            {
                                                type:
                                                    HeaderComponent.CLEAR_FILTERS,
                                                options: {
                                                    label:
                                                        'Clear Search & Filters',
                                                    icon: {
                                                        filename: 'CloseIcon',
                                                        position: 'after',
                                                    },
                                                },
                                            },
                                            {
                                                type:
                                                    HeaderComponent.TOGGLE_FILTERS,
                                                options: {
                                                    label: 'Filters',
                                                    icon: {
                                                        filename: 'MenuIcon',
                                                    },
                                                    showCount: true,
                                                },
                                            },
                                        ],
                                        lg: [
                                            {
                                                type: HeaderComponent.SEARCH,
                                            },
                                            {
                                                type: HeaderComponent.DOWNLOAD,
                                            },
                                            {
                                                type: HeaderComponent.WATCHLIST,
                                            },
                                            {
                                                type:
                                                    HeaderComponent.CLEAR_FILTERS,
                                                options: {
                                                    label:
                                                        'Clear Search & Filters',
                                                    icon: {
                                                        filename: 'CloseIcon',
                                                        position: 'after',
                                                    },
                                                },
                                            },
                                            {
                                                type:
                                                    HeaderComponent.TOGGLE_FILTERS,
                                                options: {
                                                    label: 'Filters',
                                                    icon: {
                                                        filename: 'MenuIcon',
                                                    },
                                                    showCount: true,
                                                },
                                            },
                                        ],
                                    },
                                },
                                switchToCardView: 'md',
                            },
                            documentGroupMeta: 'document_type',
                            documentGroupOrder: ['KIID', 'Factsheet'],
                            noDataPlaceholder: '--',
                            fundCurrencyProperty: 'ccy',
                            defaultCulture: 'en-GB',
                            identifierSearch: {
                                properties: ['isin', 'fund_name'],
                                hide: false,
                                fuzzy: false,
                                placeholder: {
                                    format: {
                                        singular: 'Search by {properties}',
                                        plural: 'Search by {properties}',
                                    },
                                    badge: {
                                        show: true,
                                        align: 'left',
                                        format: {
                                            singular: '{total} share class',
                                            plural: '{total} share classes',
                                        },
                                    },
                                    aliases: [
                                        {
                                            key: 'isin',
                                            value:
                                                'International Securities Identification Number',
                                        },
                                    ],
                                },
                            },
                            searchLimit: 4,
                            autoSearchMinChar: 3,
                            sort: [
                                {
                                    key: 'fund_name',
                                    direction: 'ASC',
                                },
                                {
                                    key: 'primary_sort_{app:fundlist}',
                                    direction: 'ASC',
                                },
                                {
                                    key: 'isin',
                                    direction: 'ASC',
                                },
                            ],
                            advancedSort: {
                                type: 'Statistics',
                                code: 'srri',
                                key: 'Risk',
                                direction: 'ASC',
                            },
                            performanceCheck: {
                                startDate: {
                                    property: 'timeseries',
                                    code: 'prices',
                                    key: 'date',
                                    index: 0,
                                },
                                endDate: {
                                    property: 'timeseries',
                                    code: 'prices',
                                    key: 'date',
                                    index: -1,
                                },
                                value: 36,
                                period: 'MONTHS',
                                operator: '>',
                            },
                            documentsJoinSpec: [
                                {
                                    joinTo: 'isin',
                                    joinFrom: 'isin',
                                },
                                {
                                    joinTo: 'fund_name',
                                    joinFrom: 'fund_name',
                                },
                            ],
                            excludeExpiredDocuments: true,
                            globalDocumentSearch: [
                                {
                                    property: 'isin',
                                    matchtype: 'MATCH',
                                    values: ['All'],
                                },
                            ],
                            documentSegmentationFilters: {
                                All: {
                                    inclusionClauses: [
                                        {
                                            name: 'test',
                                            description: 'test',
                                            clauses: [
                                                {
                                                    metatype: 'document_type',
                                                    operator: '=',
                                                    value: 'Other',
                                                },
                                            ],
                                        },
                                        {
                                            name: 'test',
                                            description: 'test',
                                            clauses: [
                                                {
                                                    metatype: 'document_type',
                                                    operator: '=',
                                                    value: [
                                                        'Factsheet',
                                                        'KIID',
                                                    ],
                                                },
                                                {
                                                    property: 'performance_fee',
                                                    operator: '>=',
                                                    value: 0.2,
                                                },
                                                {
                                                    startDate: {
                                                        property: 'timeseries',
                                                        code: 'prices',
                                                        key: 'date',
                                                        index: 0,
                                                    },
                                                    endDate: {
                                                        property: 'timeseries',
                                                        code: 'prices',
                                                        key: 'date',
                                                        index: -1,
                                                    },
                                                    value: 36,
                                                    period: 'MONTHS',
                                                    operator: '>',
                                                },
                                            ],
                                        },
                                    ],
                                },
                            },
                            documentFilename: {
                                query: {
                                    queryOptionsType: 'none',
                                    subQueries: [
                                        {
                                            code: 'fund_name',
                                            entityType: 'CLSS',
                                            queryId: 'fundName',
                                            queryOptionsType: 'property',
                                        },
                                        {
                                            key: 'meta.document_type.value',
                                            queryId: 'docType',
                                            queryOptionsType: 'response',
                                        },
                                        {
                                            key: 'cultureCode',
                                            queryId: 'culture',
                                            queryOptionsType: 'response',
                                        },
                                    ],
                                    value:
                                        '{sub:fundName}-{sub:docType}-{sub:culture}',
                                },
                            },
                            cultureSearchTransform: {
                                'en-GB': ['en-DK', 'en-DE'],
                            },
                            columnConfig: [
                                {
                                    label: 'Share Class',
                                    data: [
                                        {
                                            property: 'properties_pub',
                                            code: 'short_name',
                                            key: 'value',
                                            separator: ' / ',
                                            sortable: false,
                                        },
                                        {
                                            property: 'statistics',
                                            code: 'srri',
                                            key: 'value',
                                            index: 0,
                                            sortKey: 'Risk',
                                            separator: ' / ',
                                            sortable: true,
                                        },
                                        {
                                            property: 'properties_pub',
                                            code: 'class_code',
                                            key: 'value',
                                            sortable: false,
                                        },
                                    ],
                                    width: '20%',
                                    verticalAlign: 'bottom',
                                    disclaimerTypes: ['Fees'],
                                    url: 'filteredFundPage',
                                    wrapText: true,
                                    cellDisclaimers: [
                                        {
                                            disclaimerType: 'Fees',
                                            display: 'superscript',
                                        },
                                        {
                                            disclaimerType: 'Past Perf',
                                            display: 'superscript',
                                        },
                                        {
                                            disclaimerType: 'Morningstar',
                                            display: 'superscript',
                                        },
                                        {
                                            disclaimerType: 'Fees',
                                            display: 'tooltip',
                                        },
                                    ],
                                },
                                {
                                    label: 'ISIN',
                                    property: 'isin',
                                    textAlign: 'left',
                                    width: '15%',
                                    verticalAlign: 'bottom',
                                    sortable: true,
                                    url: {
                                        url:
                                            '/{app:sitelocale}/{app:audience}/{fund_name}-{share_class_name}',
                                        queries: [
                                            {
                                                key: 'fund_name',
                                                query: {
                                                    queryOptionsType:
                                                        'property',
                                                    entityType: 'CLSS',
                                                    code: 'fund_name',
                                                    transforms: [
                                                        {
                                                            transformOptionsType:
                                                                'alias',
                                                            aliases: [
                                                                {
                                                                    source: '&',
                                                                },
                                                            ],
                                                        },
                                                        {
                                                            transformOptionsType:
                                                                'case',
                                                            caseType: 'param',
                                                        },
                                                    ],
                                                },
                                            },
                                            {
                                                queryId: 'share_class_name',
                                                queryOptionsType: 'property',
                                                entityType: 'CLSS',
                                                code: 'public_share_class_name',
                                                transforms: [
                                                    {
                                                        transformOptionsType:
                                                            'alias',
                                                        aliases: [
                                                            {
                                                                source: '&',
                                                            },
                                                        ],
                                                    },
                                                    {
                                                        transformOptionsType:
                                                            'case',
                                                        caseType: 'param',
                                                    },
                                                ],
                                            },
                                        ],
                                        disabledFlag: {
                                            conditional: {
                                                conditions: [
                                                    {
                                                        field:
                                                            'fundPageActiveStatus',
                                                        operator: '=',
                                                        value: 'inactive',
                                                    },
                                                ],
                                            },
                                            queries: [
                                                {
                                                    queryId:
                                                        'fundPageActiveStatus',
                                                    queryOptionsType:
                                                        'property',
                                                    entityType: 'CLSS',
                                                    code:
                                                        'fund_page_active_status',
                                                },
                                            ],
                                        },
                                        fundListCheck: 'ukAdvisor',
                                    },
                                    cellDisclaimers: [
                                        {
                                            disclaimerType:
                                                'KIID Practical Information',
                                            display: 'superscript',
                                            linked: true,
                                            conditional: {
                                                conditions: [
                                                    {
                                                        field: 'clientCode',
                                                        value: 'KS1234948062',
                                                    },
                                                ],
                                            },
                                        },
                                    ],
                                    isRowIdentifier: true,
                                },
                                {
                                    label: 'Morningstar Rating',
                                    responsiveConfig: {
                                        sm: {
                                            label: 'M-Star',
                                        },
                                        md: {
                                            label: 'Morning*',
                                        },
                                    },
                                    property: 'morningstar_rating',
                                    type: 'stars',
                                    width: '10%',
                                    textAlign: 'left',
                                    verticalAlign: 'bottom',
                                    noDataPlaceholder: 'n/a',
                                    disabledEntityCheck: true,
                                },
                                {
                                    type: 'dropdown',
                                    groups: [
                                        {
                                            type: 'group',
                                            label:
                                                'Discrete Performance (12M Rolling)',
                                            columns: [
                                                {
                                                    label: '1Y',
                                                    data: {
                                                        property: 'statistics',
                                                        code:
                                                            'performance_annual_returns',
                                                        key: 'value',
                                                        index: -1,
                                                        showChange: {
                                                            showIcon: false,
                                                            changeUpColor:
                                                                'primary',
                                                            changeDownColor:
                                                                'warning',
                                                        },
                                                        format: {
                                                            id: 'number',
                                                        },
                                                    },
                                                    verticalAlign: 'bottom',
                                                    sortable: true,
                                                    disabledEntityCheck: true,
                                                },
                                                {
                                                    label: '2Y',
                                                    data: {
                                                        property: 'statistics',
                                                        code:
                                                            'performance_annual_returns',
                                                        key: 'value',
                                                        index: -2,
                                                        showChange: true,
                                                    },
                                                    verticalAlign: 'bottom',
                                                    disabledEntityCheck: true,
                                                },
                                                {
                                                    label: '3Y',
                                                    data: {
                                                        property: 'statistics',
                                                        code:
                                                            'performance_annual_returns',
                                                        key: 'value',
                                                        index: -3,
                                                        showChange: true,
                                                    },
                                                    verticalAlign: 'bottom',
                                                    disabledEntityCheck: true,
                                                },
                                                {
                                                    label: '4Y',
                                                    data: {
                                                        property: 'statistics',
                                                        code:
                                                            'performance_annual_returns',
                                                        key: 'value',
                                                        index: -4,
                                                        showChange: true,
                                                    },
                                                    verticalAlign: 'bottom',
                                                    disabledEntityCheck: true,
                                                },
                                                {
                                                    label: '5Y',
                                                    data: {
                                                        property: 'statistics',
                                                        code:
                                                            'performance_annual_returns',
                                                        key: 'value',
                                                        index: -5,
                                                        showChange: true,
                                                    },
                                                    disclaimerTypes: ['Fees'],
                                                    verticalAlign: 'bottom',
                                                    sortable: true,
                                                    disabledEntityCheck: true,
                                                },
                                            ],
                                            textAlign: 'center',
                                            verticalAlign: 'bottom',
                                            width: '40%',
                                            disclaimerTypes: ['Fees'],
                                        },
                                        {
                                            type: 'group',
                                            label:
                                                'Discrete Performance Dynamic',
                                            columns: [
                                                {
                                                    label: 'Y {label}',
                                                    type: 'dynamic',
                                                    labelFormat: {
                                                        type: 'date',
                                                        options: {
                                                            year: '2-digit',
                                                        },
                                                    },
                                                    responsiveConfig: {
                                                        lg: {
                                                            labelFormat: {
                                                                type: 'date',
                                                                options: {
                                                                    year:
                                                                        '2-digit',
                                                                },
                                                            },
                                                        },
                                                        md: {
                                                            labelFormat: {
                                                                type: 'date',
                                                                options: {
                                                                    year:
                                                                        '2-digit',
                                                                    month:
                                                                        '2-digit',
                                                                },
                                                            },
                                                        },
                                                    },
                                                    data: {
                                                        property: 'statistics',
                                                        code:
                                                            'performance_annual_returns',
                                                        key: 'value',
                                                        sort: {
                                                            type: 'date',
                                                            direction: 'DESC',
                                                            key: 'label',
                                                        },
                                                        startIndex: 0,
                                                        endIndex: 5,
                                                        showChange: true,
                                                    },
                                                    verticalAlign: 'bottom',
                                                    sortable: true,
                                                    disabledEntityCheck: true,
                                                },
                                            ],
                                            textAlign: 'center',
                                            verticalAlign: 'bottom',
                                            width: '40%',
                                            disclaimerTypes: ['Fees'],
                                        },
                                        {
                                            type: 'group',
                                            label: 'Prices',
                                            columns: [
                                                {
                                                    label: 'Price Date',
                                                    data: {
                                                        property: 'timeseries',
                                                        code: 'prices',
                                                        index: -1,
                                                        key: 'date',
                                                        format: {
                                                            id: 'date',
                                                        },
                                                    },
                                                    disabledEntityCheck: true,
                                                },
                                                {
                                                    label: 'Price',
                                                    data: {
                                                        property: 'timeseries',
                                                        code: 'prices',
                                                        index: -1,
                                                        key: 'value',
                                                        format: {
                                                            type: 'number',
                                                            options: {
                                                                style:
                                                                    'decimal',
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2,
                                                            },
                                                        },
                                                    },
                                                    disabledEntityCheck: true,
                                                },
                                                {
                                                    label: 'Price Change',
                                                    data: [
                                                        {
                                                            property:
                                                                'timeseries',
                                                            code: 'prices',
                                                            index: 0,
                                                            key: 'value',
                                                            format: {
                                                                type: 'number',
                                                                options: {
                                                                    style:
                                                                        'decimal',
                                                                    minimumFractionDigits: 1,
                                                                    maximumFractionDigits: 1,
                                                                },
                                                            },
                                                            separator: ' / ',
                                                        },
                                                        {
                                                            property:
                                                                'timeseries',
                                                            code: 'prices',
                                                            index: -1,
                                                            key: 'value',
                                                            format: {
                                                                type: 'number',
                                                                options: {
                                                                    style:
                                                                        'decimal',
                                                                    minimumFractionDigits: 1,
                                                                    maximumFractionDigits: 1,
                                                                },
                                                            },
                                                            showChange: true,
                                                        },
                                                    ],
                                                    disabledEntityCheck: true,
                                                },
                                            ],
                                            textAlign: 'center',
                                            verticalAlign: 'bottom',
                                            width: '40%',
                                            disclaimerTypes: ['Morningstar'],
                                        },
                                    ],
                                },
                                {
                                    type: 'document',
                                    label: 'Factsheet',
                                    documentType: 'Factsheet',
                                    verticalAlign: 'bottom',
                                    width: '15%',
                                    disabledEntityCheck: true,
                                    showSingleDocument: true,
                                    showSingleDocumentLabel: true,
                                    index: 0,
                                    sort: {
                                        direction: 'DESC',
                                        key: 'meta.document_date.value',
                                        type: 'date',
                                    },
                                },
                                {
                                    type: 'download',
                                    label: 'Factsheet',
                                    documentType: 'Factsheet',
                                    verticalAlign: 'bottom',
                                    width: '15%',
                                    disabledEntityCheck: true,
                                    cellDisclaimers: [
                                        {
                                            disclaimerType: 'Fees',
                                            display: 'tooltip',
                                        },
                                    ],
                                },
                                {
                                    type: 'watchlist',
                                    label: 'Watchlist',
                                    width: '100px',
                                    verticalAlign: 'bottom',
                                    showLabel: true,
                                },
                            ],
                            rowConfig: {
                                type: 'FundCard',
                                ratingImageOptions: {
                                    imageQuery: {
                                        queryOptionsType: 'none',
                                        value:
                                            'afm_{sub:culture}_risicoscore_{sub:risk_rating}.svg',
                                        allSubQueryValuesRequired: true,
                                        subQueries: [
                                            {
                                                queryId: 'risk_rating',
                                                queryOptionsType: 'statistics',
                                                entityType: 'CLSS',
                                                code: 'srri',
                                                key: 'value',
                                                index: 0,
                                            },
                                            {
                                                queryId: 'culture',
                                                queryOptionsType: 'input',
                                                key: 'culture',
                                                transforms: [
                                                    {
                                                        transformOptionsType:
                                                            'alias',
                                                        aliases: [
                                                            {
                                                                source: 'en-GB',
                                                                replacement:
                                                                    'English',
                                                            },
                                                        ],
                                                    },
                                                    {
                                                        transformOptionsType:
                                                            'case',
                                                        caseType: 'lower',
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    asAtDate: {
                                        label: 'As at',
                                        data: {
                                            property: 'properties_pub',
                                            code: 'fund_launch_date',
                                            key: 'value',
                                        },
                                    },
                                    disclaimers: [
                                        {
                                            disclaimerType: 'KIID Risks',
                                            linked: true,
                                        },
                                    ],
                                    headingTextQuery: {
                                        queryOptionsType: 'none',
                                        value: 'Risk Rating',
                                    },
                                    noData: {
                                        placeholder: 'No Data',
                                        hideComponent: false,
                                    },
                                    noImage: {
                                        defaultImageQuery: {
                                            queryOptionsType: 'none',
                                            value:
                                                'AFM_english_geen-risicoscore.jpg',
                                        },
                                        placeholder: 'No Image',
                                    },
                                },
                                header: {
                                    nameProperty: 'public_share_class_name',
                                    linkProperty: '',
                                    linkLabel: 'Fund Page',
                                    url: 'filteredFundPage',
                                },
                                fundCommentary: {
                                    label: 'Investment Objective',
                                    commentaries: [
                                        {
                                            commentaryType: 'Fund Commentary',
                                        },
                                    ],
                                    showReadMoreLess: true,
                                    readMoreLessOptions: {
                                        readMoreCommentaries: [
                                            {
                                                commentaryType:
                                                    'Alt Investment Objective',
                                            },
                                        ],
                                        readMoreText: 'Read More',
                                        readMoreIcon: 'ReadMoreIcon.svg',
                                        readLessText: 'Read Less',
                                        readLessIcon: 'ReadLessIcon.svg',
                                        align: 'center',
                                        alignIcon: 'left',
                                        height: '55px',
                                    },
                                },
                                fundFacts: {
                                    label: 'Fund Facts',
                                    cards: [
                                        {
                                            label: 'Price (NAV)',
                                            primary: true,
                                            data: {
                                                property: 'timeseries',
                                                code: 'prices',
                                                index: -1,
                                                key: 'value',
                                                format: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    },
                                                },
                                            },
                                            asAtDate: {
                                                label: 'As at',
                                                data: {
                                                    property: 'timeseries',
                                                    code: 'returns',
                                                    ccy: 'NA',
                                                    key: 'date',
                                                    index: 0,
                                                    format: {
                                                        type: 'date',
                                                        options: {
                                                            year: '2-digit',
                                                            month: 'short',
                                                            day: '2-digit',
                                                        },
                                                    },
                                                },
                                            },
                                            disclaimer: {
                                                disclaimerType: 'KIID Charges',
                                                linked: true,
                                                footnote: false,
                                            },
                                        },
                                        {
                                            label: 'Bid Price',
                                            primary: true,
                                            data: {
                                                property: 'properties_pub',
                                                code: 'bid_price',
                                                noDataPlaceholder: 'n/a',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: true,
                                            },
                                            asAtDate: {
                                                label: 'As at',
                                                data: {
                                                    property: 'properties_pub',
                                                    code: 'fund_launch_date',
                                                    key: 'value',
                                                },
                                            },
                                        },
                                        {
                                            label: 'Offer Price',
                                            primary: true,
                                            data: {
                                                property: 'properties_pub',
                                                code: 'offer_price',
                                                noDataPlaceholder: 'n/a',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'Price Change',
                                            primary: true,
                                            data: [
                                                {
                                                    property: 'timeseries',
                                                    code: 'prices',
                                                    index: 0,
                                                    key: 'value',
                                                    format: {
                                                        type: 'number',
                                                        options: {
                                                            style: 'decimal',
                                                            minimumFractionDigits: 1,
                                                            maximumFractionDigits: 1,
                                                        },
                                                    },
                                                    separator: ' / ',
                                                },
                                                {
                                                    property: 'timeseries',
                                                    code: 'prices',
                                                    index: -1,
                                                    key: 'value',
                                                    format: {
                                                        type: 'number',
                                                        options: {
                                                            style: 'decimal',
                                                            minimumFractionDigits: 1,
                                                            maximumFractionDigits: 1,
                                                        },
                                                    },
                                                    showChange: {
                                                        showIcon: true,
                                                        changeUpColor: 'green',
                                                        changeDownColor: 'red',
                                                    },
                                                    changeIcon: {
                                                        align: 'right',
                                                        up:
                                                            'FundFactChangeUpIcon.svg',
                                                        down:
                                                            'FundFactChangeDownIcon.svg',
                                                    },
                                                },
                                            ],
                                            changeIcon: {
                                                align: 'right',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'KIID Risks',
                                                linked: true,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'SRRI',
                                            higherRiskLabel: 'High',
                                            lowerRiskLabel: 'Low',
                                            primary: true,
                                            ui: 'srri',
                                            data: {
                                                property: 'statistics',
                                                code: 'srri',
                                                index: 0,
                                                key: 'value',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'KIID Risks',
                                                linked: true,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'Morningstar Rating',
                                            primary: true,
                                            property: 'morningstar_rating',
                                            ui: 'stars',
                                            disclaimer: {
                                                disclaimerType: 'KIID Risks',
                                                linked: true,
                                                footnote: true,
                                            },
                                            noDataPlaceholder: 'n/a',
                                        },
                                        {
                                            label: 'AUM',
                                            primary: true,
                                            property: 'aum',
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: false,
                                            },
                                        },
                                        {
                                            label: 'Ongoing Charge',
                                            primary: true,
                                            data: {
                                                property: 'properties_pub',
                                                code: 'management_charge',
                                                key: 'value',
                                                format: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    },
                                                },
                                            },
                                        },
                                        {
                                            label: 'Yield',
                                            property: 'yield',
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'SRRI',
                                            ui: 'srri',
                                            data: {
                                                property: 'statistics',
                                                code: 'srri',
                                                index: 0,
                                                key: 'value',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'KIID Risks',
                                                linked: true,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'AUM',
                                            property: 'aum',
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: false,
                                            },
                                            asAtDate: {
                                                label: 'As at',
                                                data: {
                                                    property: 'properties_pub',
                                                    code: 'fund_launch_date',
                                                    key: 'value',
                                                },
                                            },
                                        },
                                        {
                                            label: 'Ongoing Charge',
                                            data: {
                                                property: 'properties_pub',
                                                code: 'management_charge',
                                                key: 'value',
                                                format: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    },
                                                },
                                            },
                                        },
                                        {
                                            label: 'Pay Dates',
                                            data: [
                                                {
                                                    property: 'properties_pub',
                                                    code: 'inception_date',
                                                    key: 'value',
                                                    separator: ', ',
                                                },
                                                {
                                                    property: 'properties_pub',
                                                    code: 'launch_date',
                                                    key: 'value',
                                                    separator: ', ',
                                                },
                                                {
                                                    property: 'properties_pub',
                                                    code: 'inception_date',
                                                    key: 'value',
                                                    separator: ', ',
                                                },
                                                {
                                                    property: 'properties_pub',
                                                    code: 'launch_date',
                                                    key: 'value',
                                                },
                                            ],
                                        },
                                        {
                                            label: 'Custom Name 1',
                                            data: {
                                                property: 'properties_pub',
                                                code: 'benchmark_names',
                                                keyIndex: 0,
                                            },
                                        },
                                        {
                                            label: 'Custom Name 2',
                                            data: {
                                                property: 'properties_pub',
                                                code: 'benchmark_names',
                                                keyIndex: 1,
                                            },
                                        },
                                        {
                                            label: 'Custom Name 3',
                                            data: {
                                                property: 'properties_pub',
                                                code: 'benchmark_names',
                                                keyIndex: 2,
                                            },
                                        },
                                    ],
                                    disclaimers: [
                                        {
                                            disclaimerType: 'Fees',
                                            linked: false,
                                        },
                                        {
                                            disclaimerType: 'KIID Charges',
                                            linked: true,
                                        },
                                    ],
                                },
                                fundManagers: {
                                    label: 'Fund Managers',
                                    showImage: true,
                                    showBiography: true,
                                    showExtended: [
                                        {
                                            property: 'title',
                                        },
                                    ],
                                    link: {
                                        label: 'Read Bio',
                                        property: 'bio_link',
                                    },
                                    url: {
                                        url: 'https://www.kurtosys.com/{code}',
                                        target: '_blank',
                                        queries: [
                                            {
                                                queryId: 'code',
                                                queryOptionsType: 'fundManager',
                                                key: 'extended',
                                                extendedKey: 'nationality',
                                            },
                                        ],
                                    },
                                    sort: {
                                        key: 'extended.nationality',
                                        type: 'string',
                                        direction: 'ASC',
                                    },
                                    autoPlay: 3000,
                                },
                                charts: {
                                    label: 'Performance',
                                    config: [
                                        {
                                            id: 'cumulativePerformance',
                                            label: 'Cumulative Performance',
                                            selected: false,
                                            grouped: true,
                                            performanceCheck: true,
                                            type: 'LineChart',
                                            data: {
                                                property: 'timeseries',
                                                code: 'returns',
                                                ccy: 'NA',
                                                classification: 'NA',
                                                periodicity: 'MONTHLY',
                                            },
                                            config: {
                                                legend: true,
                                                grid: {
                                                    stroke: 'gray300',
                                                    vertical: false,
                                                },
                                                tooltip: {
                                                    labelFormat: {
                                                        type: 'date',
                                                        options: {
                                                            year: '2-digit',
                                                            month: 'short',
                                                        },
                                                    },
                                                    valueFormat: {
                                                        type: 'currency',
                                                        options: {
                                                            style: 'currency',
                                                            currency: 'GBP',
                                                            currencyDisplay:
                                                                'code',
                                                            minimumFractionDigits: 1,
                                                            maximumFractionDigits: 2,
                                                        },
                                                    },
                                                },
                                                xAxis: {
                                                    dataKey: 'date',
                                                    domain: [
                                                        'dataMin',
                                                        'dataMax',
                                                    ],
                                                    format: {
                                                        type: 'date',
                                                        options: {
                                                            year: '2-digit',
                                                            month: 'short',
                                                        },
                                                    },
                                                },
                                                yAxis: {
                                                    label: {
                                                        value: 'Price',
                                                        angle: -90,
                                                        position: 'insideLeft',
                                                    },
                                                    dataKey: 'value',
                                                    basePoint: 100,
                                                    tickCount: 5,
                                                },
                                                series: [
                                                    {
                                                        dataKey: 'value',
                                                        color: 'primary',
                                                        name: 'shareclass',
                                                        nameQuery: {
                                                            queryOptionsType:
                                                                'property',
                                                            entityType: 'CLSS',
                                                            code: 'share_class',
                                                            index: 0,
                                                        },
                                                    },
                                                    {
                                                        dataKey:
                                                            'benchmarkReturn2',
                                                        color: 'secondary',
                                                        name:
                                                            'benchmarkReturn2',
                                                        connectNulls: true,
                                                        nameQuery: {
                                                            queryOptionsType:
                                                                'statistics',
                                                            entityType: 'CLSS',
                                                            code: 'benchmarks',
                                                            key: 'label',
                                                            index: 0,
                                                            collectionItemValueConditional: {
                                                                conditions: [
                                                                    {
                                                                        field:
                                                                            'value',
                                                                        value: 2,
                                                                    },
                                                                ],
                                                            },
                                                        },
                                                    },
                                                ],
                                                rebase: {
                                                    enabled: true,
                                                    postTransforms: [
                                                        {
                                                            transformOptionsType:
                                                                'number',
                                                            numberOptions: {
                                                                operator:
                                                                    'subtract',
                                                                value: 100,
                                                            },
                                                        },
                                                    ],
                                                },
                                                rebaseQueries: {
                                                    series: [
                                                        {
                                                            key: 'value',
                                                        },
                                                        {
                                                            key:
                                                                'benchmarkReturn2',
                                                        },
                                                    ],
                                                },
                                            },
                                            csvDownload: {
                                                show: true,
                                                align: 'left',
                                                label: {
                                                    property: 'fund_name',
                                                },
                                                iconOptions: {
                                                    filename: 'DownloadIcon',
                                                    hide: false,
                                                    position: 'before',
                                                },
                                                filename:
                                                    'Cumulative Performance',
                                                columns: [
                                                    {
                                                        label: 'Date as at',
                                                        dataKey: 'date',
                                                        format: {
                                                            type: 'date',
                                                            options: {
                                                                day: '2-digit',
                                                                month:
                                                                    '2-digit',
                                                            },
                                                        },
                                                    },
                                                    {
                                                        label: 'Shareclass',
                                                        dataKey: 'value',
                                                    },
                                                    {
                                                        label: 'Benchmark2',
                                                        dataKey:
                                                            'benchmarkReturn2',
                                                    },
                                                ],
                                            },
                                            asAtDate: {
                                                label: 'As at',
                                                data: {
                                                    property: 'timeseries',
                                                    code: 'returns',
                                                    ccy: 'NA',
                                                    key: 'date',
                                                    index: -1,
                                                },
                                                format: {
                                                    type: 'date',
                                                    options: {
                                                        year: '2-digit',
                                                        month: 'short',
                                                        day: '2-digit',
                                                    },
                                                },
                                            },
                                            dateOptions: {
                                                xAxisDateAsNumber: true,
                                                xAxisTickOptions: {
                                                    tickCount: 5,
                                                    periodOptions: [
                                                        {
                                                            periodKey: 'MONTHS',
                                                            periodValue: 3,
                                                            tickCount: 3,
                                                        },
                                                        {
                                                            periodKey: 'MONTHS',
                                                            periodValue: 6,
                                                            tickCount: 6,
                                                        },
                                                        {
                                                            periodKey: 'YEARS',
                                                            periodValue: 5,
                                                            tickCount: 5,
                                                        },
                                                        {
                                                            periodKey: 'YEARS',
                                                            periodValue: 10,
                                                            tickCount: 10,
                                                        },
                                                    ],
                                                },
                                            },
                                            chartControls: {
                                                dateProperty: 'date',
                                                periodSelector: {
                                                    ui: 'buttons',
                                                    noDataForPeriodUi:
                                                        'disabled',
                                                    options: [
                                                        {
                                                            label: '1 Month',
                                                            period: 'MONTHS',
                                                            value: 1,
                                                        },
                                                        {
                                                            label: '3 Months',
                                                            period: 'MONTHS',
                                                            value: 3,
                                                        },
                                                        {
                                                            label: '6 Months',
                                                            period: 'MONTHS',
                                                            value: 6,
                                                        },
                                                        {
                                                            label: '3 Years',
                                                            period: 'YEARS',
                                                            value: 3,
                                                        },
                                                        {
                                                            label: '5 Years',
                                                            period: 'YEARS',
                                                            value: 5,
                                                        },
                                                        {
                                                            label: '10 Years',
                                                            period: 'YEARS',
                                                            value: 10,
                                                        },
                                                        {
                                                            label: 'YTD',
                                                            period: 'YTD',
                                                        },
                                                        {
                                                            label:
                                                                'Since Inception',
                                                            selected: true,
                                                        },
                                                    ],
                                                },
                                                dateSelector: {
                                                    ui: 'dropdown',
                                                    format: {
                                                        type: 'date',
                                                        options: {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: '2-digit',
                                                        },
                                                    },
                                                    startDateLabel:
                                                        'Start date',
                                                    endDateLabel: 'End date',
                                                },
                                            },
                                            performanceCheckDisclaimerTypes: [
                                                {
                                                    disclaimerType:
                                                        'KIID Charges',
                                                    linked: true,
                                                },
                                            ],
                                            noDataDisclaimerTypes: [
                                                {
                                                    disclaimerType: 'Fees',
                                                    linked: false,
                                                },
                                            ],
                                            disclaimerTypes: [
                                                {
                                                    disclaimerType:
                                                        'KIID Charges',
                                                    linked: true,
                                                },
                                            ],
                                        },
                                        {
                                            id: 'annualisedPerformance',
                                            label: 'Annualised Performance',
                                            selected: false,
                                            grouped: true,
                                            type: 'BarChart',
                                            data: {
                                                property: 'statistics',
                                                code:
                                                    'performance_annual_returns',
                                                ccy: 'NA',
                                                sort: {
                                                    type: 'string',
                                                    key: 'label',
                                                    direction: 'ASC',
                                                },
                                            },
                                            config: {
                                                legend: true,
                                                showBlankSeries: false,
                                                grid: {
                                                    stroke: 'gray300',
                                                    vertical: false,
                                                },
                                                barChart: {
                                                    barSize: 20,
                                                    barGap: 5,
                                                    barCategoryGap: 10,
                                                    maxBarSize: 56,
                                                    referenceLines: [
                                                        {
                                                            y: 0,
                                                            stroke: 'black',
                                                            strokeWidth: 2,
                                                        },
                                                        {
                                                            y: 5,
                                                            stroke: 'green',
                                                            strokeWidth: 1,
                                                        },
                                                        {
                                                            y: -5,
                                                            stroke: 'red',
                                                            strokeWidth: 1,
                                                        },
                                                    ],
                                                },
                                                tooltip: {
                                                    valueFormat: {
                                                        type: 'number',
                                                        options: {
                                                            style: 'decimal',
                                                            minimumFractionDigits: 1,
                                                        },
                                                    },
                                                },
                                                xAxis: {
                                                    dataKey: 'label',
                                                    type: 'category',
                                                    label: {
                                                        value: 'Year',
                                                    },
                                                },
                                                yAxis: {
                                                    label: {
                                                        value: 'Percentage %',
                                                        angle: -90,
                                                        position: 'insideLeft',
                                                    },
                                                    type: 'number',
                                                },
                                                series: [
                                                    {
                                                        name: 'Fund',
                                                        nameProperty:
                                                            'fund_name',
                                                        dataKey: 'value',
                                                        fill: 'primary',
                                                        unit: '%',
                                                    },
                                                    {
                                                        name: 'Benchmark',
                                                        dataKey: 'value',
                                                        fill: 'gray500',
                                                        unit: '%',
                                                    },
                                                    {
                                                        dataKey:
                                                            'bmark2_return',
                                                        color: 'success',
                                                        name: 'Benchmark2',
                                                        nameProperty:
                                                            'benchmark2_name',
                                                    },
                                                ],
                                            },
                                            noDataDisclaimerTypes: [
                                                {
                                                    disclaimerType:
                                                        'KIID Charges',
                                                    linked: true,
                                                },
                                                {
                                                    disclaimerType: 'Fees',
                                                    linked: false,
                                                },
                                            ],
                                            disclaimerTypes: [
                                                {
                                                    disclaimerType:
                                                        'KIID Past Perf',
                                                    linked: true,
                                                },
                                                {
                                                    disclaimerType: 'Fees',
                                                    linked: false,
                                                },
                                            ],
                                        },
                                        {
                                            id: 'cumulativePerformance2',
                                            label: 'Cumulative Performance 2',
                                            selected: false,
                                            grouped: false,
                                            type: 'LineChart',
                                            data: {
                                                property: 'timeseries',
                                                code: 'returns',
                                                ccy: 'NA',
                                                classification: 'NA',
                                                periodicity: 'MONTHLY',
                                            },
                                            config: {
                                                legend: true,
                                                grid: {
                                                    vertical: false,
                                                },
                                                tooltip: {
                                                    labelFormat: {
                                                        type: 'date',
                                                        options: {
                                                            day: '2-digit',
                                                            month: 'long',
                                                            year: 'numeric',
                                                        },
                                                    },
                                                    valueFormat: {
                                                        type: 'number',
                                                        options: {
                                                            style: 'decimal',
                                                            minimumFractionDigits: 3,
                                                        },
                                                    },
                                                },
                                                xAxis: {
                                                    dataKey: 'date',
                                                    format: {
                                                        type: 'date',
                                                        options: {
                                                            year: '2-digit',
                                                            month: 'short',
                                                        },
                                                    },
                                                },
                                                yAxis: {
                                                    format: {
                                                        type: 'number',
                                                        options: {
                                                            style: 'decimal',
                                                            minimumFractionDigits: 3,
                                                        },
                                                    },
                                                    basePoint: 100,
                                                    tickCount: 5,
                                                },
                                                series: [
                                                    {
                                                        dataKey:
                                                            'benchmarkReturn',
                                                        color: 'primary',
                                                        name: 'benchmarkReturn',
                                                        nameQuery: {
                                                            queryOptionsType:
                                                                'property',
                                                            entityType: 'CLSS',
                                                            code:
                                                                'benchmark_names',
                                                            index: 0,
                                                        },
                                                    },
                                                    {
                                                        dataKey:
                                                            'benchmarkReturn2',
                                                        color: 'secondary',
                                                        name:
                                                            'benchmarkReturn2',
                                                        connectNulls: true,
                                                        nameQuery: {
                                                            queryOptionsType:
                                                                'statistics',
                                                            entityType: 'CLSS',
                                                            code: 'benchmarks',
                                                            key: 'label',
                                                            index: 0,
                                                            collectionItemValueConditional: {
                                                                conditions: [
                                                                    {
                                                                        field:
                                                                            'value',
                                                                        value: 2,
                                                                    },
                                                                ],
                                                            },
                                                        },
                                                    },
                                                ],
                                                rebase: {
                                                    enabled: true,
                                                    startValue: 100,
                                                    series: [
                                                        {
                                                            key:
                                                                'benchmarkReturn',
                                                        },
                                                        {
                                                            key:
                                                                'benchmarkReturn2',
                                                        },
                                                    ],
                                                },
                                            },
                                            noDataDisclaimerTypes: [
                                                'No Chart Data',
                                            ],
                                            disclaimerTypes: ['Past Perf'],
                                        },
                                    ],
                                },
                                keyDocuments: {
                                    label: 'Key Documents',
                                    documentTypes: [
                                        'Factsheet',
                                        'KIID',
                                        'Other',
                                    ],
                                    linkProperty: 'long_name',
                                    linkLabel: 'View all documents',
                                    showIcon: false,
                                    showSingleDocument: true,
                                    showSingleDocumentLabel: false,
                                    showSingleDocumentGroupLabel: true,
                                    links: [
                                        {
                                            label: 'View single property',
                                            linkProperty: 'long_name',
                                        },
                                        {
                                            label: 'View fund page link',
                                            url: 'fundPage',
                                        },
                                        {
                                            label: 'View default icon',
                                            linkProperty: 'long_name',
                                            showIcon: true,
                                        },
                                        {
                                            label: 'View custom icon',
                                            url: 'https://kurtosys.com',
                                            showIcon: true,
                                            iconFilename: 'DownloadIcon.svg',
                                        },
                                    ],
                                },
                                footnoteLinks: false,
                                footnoteScope: 'shared',
                                footnoteScheme: 'symbol',
                                footnoteSymbolFallback: 'symbolIncrement',
                                footnoteCharacters: ['*'],
                                disclaimers: [
                                    {
                                        disclaimerType:
                                            'KIID Practical Information',
                                        linked: true,
                                    },
                                ],
                                expander: {
                                    align: 'left',
                                    collapseIconFileName: 'CollapseIcon',
                                    expandIconFileName: 'ExpandIcon',
                                    disabledFlag: {
                                        conditional: {
                                            conditions: [
                                                {
                                                    field:
                                                        'fundCardActiveStatus',
                                                    operator: '=',
                                                    value: 'inactive',
                                                },
                                            ],
                                        },
                                        queries: [
                                            {
                                                queryId: 'fundCardActiveStatus',
                                                queryOptionsType: 'property',
                                                entityType: 'CLSS',
                                                code: 'active_status',
                                            },
                                        ],
                                    },
                                    disabledTooltipDisclaimer: {
                                        queryOptionsType: 'disclaimers',
                                        key: 'Fund Card Disabled',
                                    },
                                    fundListCheck: 'ukAdvisor',
                                    showColumnHeader: true,
                                    displayType: ['icon', 'text'],
                                    label: 'Expand',
                                },
                            },
                            filterConfig: {
                                top: true,
                                topExpanded: true,
                                topExpandable: true,
                                clearIndividualFilters: {
                                    enabled: true,
                                    confirmation: 'Link Has Been Copied',
                                    label: 'Share',
                                    show: true,
                                    align: 'right',
                                },
                                clearFiltersLabel: 'Clear My Filters',
                                noDataDisplay: 'hide',
                                sortActiveFirst: true,
                                filters: [
                                    {
                                        type: 'CLSS',
                                        property: 'fund_name',
                                        label: 'Fund Name',
                                        ui: 'checklist',
                                        enableSearch: false,
                                        saveValueAsUserPreference: true,
                                    },
                                    {
                                        type: 'CLSS',
                                        property: 'isin',
                                        label: 'ISIN',
                                        ui: 'checklist',
                                        enableSearch: true,
                                        saveValueAsUserPreference: true,
                                        allowInactiveSelection: false,
                                    },
                                    {
                                        type: 'CLSS',
                                        property: 'morningstar_rating',
                                        label: 'Morningstar Rating',
                                        ui: 'stars',
                                        allowInactiveSelection: false,
                                        allowedValues: ['2', '3', '4', '5'],
                                    },
                                    {
                                        type: 'CLSS',
                                        property: 'ccy',
                                        label: 'Currency',
                                        ui: 'checklist',
                                        enableSearch: true,
                                        valueAliases: {
                                            GBP: 'British Pound',
                                            USD: 'US Dollar',
                                            CHF: 'Swiss Franc',
                                            EUR: 'Euro',
                                        },
                                    },
                                    {
                                        type: 'CLSS',
                                        property: 'distribution_type',
                                        label: 'Distribution Type',
                                        ui: 'checklist',
                                        enableSearch: true,
                                    },
                                    {
                                        type: 'CLSS',
                                        property: 'class_code',
                                        label: 'Class Code',
                                        ui: 'checklist',
                                        enableSearch: true,
                                    },
                                    {
                                        type: 'DOCUMENT_META',
                                        property: 'document_type',
                                        label: 'Document Type',
                                        ui: 'checklist',
                                        enableSearch: true,
                                        allowedValues: ['KIID', 'Factsheet'],
                                        valueAliases: {
                                            Factsheet: 'Fact Sheet',
                                            KIID:
                                                'Key Investor Information Document',
                                        },
                                    },
                                    {
                                        type: 'DOCUMENT_PROPERTY',
                                        property: 'cultureCode',
                                        label: 'Document Language',
                                        ui: 'checklist',
                                        enableSearch: true,
                                        allowedValues: [
                                            'en-GB',
                                            'fr-FR',
                                            'it-IT',
                                            'de-DE',
                                        ],
                                        globalValueAliases: 'cultures',
                                    },
                                    {
                                        type: 'CLSS',
                                        property: 'non_existent',
                                        label: 'Should Not Render',
                                        ui: 'checklist',
                                        enableSearch: true,
                                    },
                                ],
                            },
                            documentSearch: [
                                {
                                    property: 'document_type',
                                    values: ['Factsheet', 'KIID'],
                                    matchtype: 'MATCH',
                                },
                            ],
                            disclaimersByDocumentType: {
                                Factsheet: {
                                    disclaimersByCulture: {
                                        'en-GB': {
                                            conditionalDisclaimers: [
                                                {
                                                    disclaimerType: 'Fees',
                                                    display:
                                                        DisclaimerDisplayType.superscript,
                                                },
                                            ],
                                        },
                                    },
                                },
                            },
                            cultureAliases: {
                                'en-GB': 'English',
                                'fr-FR': 'French',
                                'de-DE': 'German',
                                'it-IT': 'Italian',
                            },
                            headerDisclaimerTypes: ['Past Perf'],
                            footerDisclaimerTypes: ['Fees', 'Past Perf'],
                            footnoteLinks: true,
                            footnoteScheme: 'symbol',
                            footnoteSymbolFallback: 'numeric',
                            footnoteCharacters: ['*', '!'],
                            explorer: {
                                watchlist: {
                                    enabled: true,
                                    hideChild: 'none',
                                    label: 'Watchlist',
                                    title: 'Toggle Watchlist',
                                    property: 'isin',
                                    scope: 'unique',
                                    key: 'performanceTab',
                                },
                                download: {
                                    enabled: true,
                                    showGroup: true,
                                    hideChild: 'none',
                                    hideChildMobile: 'icon',
                                    groupLabel: 'Fund Name',
                                    subGroupLabel: 'Fund Sub Group',
                                    disclaimerSections: {
                                        footer: {
                                            types: ['Fees', 'Past Perf'],
                                        },
                                    },
                                },
                                paging: {
                                    mode: 'loadMoreButton',
                                    hideLoadMoreButtonWhenAllRecordsRetrieved: false,
                                    infiniteScrollOptions: {
                                        threshold: 250,
                                    },
                                },
                                showChange: {
                                    showValue: true,
                                    showIcon: true,
                                    changeUpColor: 'inherit',
                                    changeDownColor: 'inherit',
                                },
                                resultsMeta: {
                                    resultSummary: {
                                        label: 'Showing {count} of {total}',
                                        align: 'left',
                                    },
                                    asAtDate: {
                                        label: 'As of {value}',
                                        align: 'right',
                                        data: {
                                            property: 'timeseries',
                                            code: 'returns',
                                            ccy: 'NA',
                                            key: 'date',
                                            index: -1,
                                            format: {
                                                type: 'date',
                                                options: {
                                                    year: '2-digit',
                                                    month: 'short',
                                                    day: '2-digit',
                                                },
                                            },
                                        },
                                    },
                                },
                            },
                            links: {
                                fundPage:
                                    '/{app:country}/{app:language}/{app:investor_type}/funds/{fund_name}/{class_id}/',
                                filteredFundPage: {
                                    url: '/filteredFundPage/thislinkishere',
                                    target: '_blank',
                                    linkSegmentationFilter: {
                                        inclusionClauses: ['Test Query'],
                                    },
                                    disabledFlag: {
                                        conditional: {
                                            conditions: [
                                                {
                                                    field:
                                                        'fund_page_active_status',
                                                    operator: '=',
                                                    value: 'inactive',
                                                },
                                            ],
                                        },
                                    },
                                },
                            },
                            fundListFilters: [
                                {
                                    key: 'ukAdvisor',
                                    segmentationFilter: {
                                        inclusionClauses: ['UK Advisor'],
                                    },
                                },
                            ],
                            stickyOptions: {
                                sm: {
                                    group: {
                                        enabled: true,
                                        offset: 0,
                                    },
                                    subGroup: {
                                        enabled: true,
                                        offset: 95,
                                    },
                                },
                                md: {
                                    group: {
                                        enabled: true,
                                        offset: 0,
                                    },
                                    subGroup: {
                                        enabled: true,
                                        offset: 64,
                                    },
                                },
                                lg: {
                                    group: {
                                        enabled: true,
                                        offset: 0,
                                    },
                                    subGroup: {
                                        enabled: true,
                                        offset: 64,
                                    },
                                    tableHeader: {
                                        enabled: true,
                                        offset: 128,
                                    },
                                },
                            },
                        } as ModuleConfig,
                    },
                    {
                        id: 'overview',
                        label: 'Overview',
                        disclaimers: [
                            {
                                disclaimerType: 'Fees',
                                display: DisclaimerDisplayType.superscript,
                            },
                            {
                                disclaimerType: 'Fund Card Disabled',
                                display: DisclaimerDisplayType.superscript,
                            },
                        ],
                        config: {
                            responsiveConfig: {
                                layout: {
                                    header: {
                                        sm: [
                                            {
                                                type: HeaderComponent.SEARCH,
                                            },
                                            {
                                                type: HeaderComponent.DOWNLOAD,
                                            },
                                            {
                                                type: HeaderComponent.WATCHLIST,
                                            },
                                        ],
                                        md: [
                                            {
                                                type: HeaderComponent.SEARCH,
                                            },
                                            {
                                                type:
                                                    HeaderComponent.CLEAR_FILTERS,
                                                options: {
                                                    label:
                                                        'Clear Search & Filters',
                                                    icon: {
                                                        filename: 'CloseIcon',
                                                        position: 'after',
                                                    },
                                                },
                                            },
                                        ],
                                        lg: [
                                            {
                                                type: HeaderComponent.SEARCH,
                                            },
                                            {
                                                type: HeaderComponent.DOWNLOAD,
                                            },
                                            {
                                                type: HeaderComponent.WATCHLIST,
                                            },
                                            {
                                                type:
                                                    HeaderComponent.CLEAR_FILTERS,
                                                options: {
                                                    label:
                                                        'Clear Search & Filters',
                                                    icon: {
                                                        filename: 'CloseIcon',
                                                        position: 'after',
                                                    },
                                                },
                                            },
                                        ],
                                    },
                                },
                                switchToCardView: 'md',
                            },
                            stickyOptions: {
                                sm: {
                                    group: {
                                        enabled: true,
                                        offset: 0,
                                    },
                                    subGroup: {
                                        enabled: true,
                                        offset: 114,
                                    },
                                },
                                md: {
                                    group: {
                                        enabled: true,
                                        offset: 0,
                                    },
                                    subGroup: {
                                        enabled: true,
                                        offset: 64,
                                    },
                                },
                                lg: {
                                    group: {
                                        enabled: true,
                                        offset: 0,
                                    },
                                    subGroup: {
                                        enabled: true,
                                        offset: 64,
                                    },
                                    tableHeader: {
                                        enabled: true,
                                        offset: 128,
                                    },
                                },
                            },
                            documentFilename: {
                                text:
                                    'overview {document_type} {isin} {document_date}',
                                dataFields: {
                                    document_date: {
                                        property: 'document_date',
                                        format: {
                                            type: 'date',
                                            options: {
                                                day: '2-digit',
                                                year: '2-digit',
                                                month: '2-digit',
                                            },
                                        },
                                    },
                                    isin: {
                                        property: 'isin',
                                    },
                                    document_type: {
                                        property: 'document_type',
                                    },
                                },
                            },
                            explorer: {
                                download: {
                                    enabled: true,
                                },
                                watchlist: {
                                    enabled: true,
                                    hideChild: 'none',
                                    label: 'Watchlist',
                                    title: 'Toggle Watchlist',
                                    property: 'isin',
                                    scope: 'global', // 'unique',
                                },
                            },
                            fundGroupProperty: 'class_code',
                            documentGroupMeta: 'document_type',
                            enableWatchlist: false,
                            defaultCulture: 'en-GB',
                            identifierSearch: {
                                properties: ['isin', 'fund_name'],
                                fuzzy: false,
                            },
                            searchLimit: 30,
                            autoSearchMinChar: 3,
                            sort: [
                                {
                                    key: 'fund_name',
                                    direction: 'ASC',
                                },
                                {
                                    key: 'isin',
                                    direction: 'ASC',
                                },
                            ],
                            documentsJoinSpec: [
                                {
                                    joinTo: 'isin',
                                    joinFrom: 'isin',
                                },
                                {
                                    joinTo: 'fund_name',
                                    joinFrom: 'fund_name',
                                },
                            ],
                            columnConfig: [
                                {
                                    label: 'Share Class',
                                    data: {
                                        property: 'properties_pub',
                                        code: 'short_name',
                                        key: 'value',
                                    },
                                },
                                {
                                    label: 'ISIN',
                                    data: {
                                        property: 'properties_pub',
                                        code: 'isin',
                                        key: 'value',
                                    },
                                },
                                {
                                    label: 'Price Date',
                                    data: {
                                        property: 'timeseries',
                                        code: 'prices',
                                        index: -1,
                                        key: 'date',
                                        format: {
                                            type: 'date',
                                            options: {
                                                day: '2-digit',
                                                year: '2-digit',
                                                month: 'short',
                                            },
                                        },
                                    },
                                },
                                {
                                    type: 'group',
                                    label: 'Prices',
                                    columns: [
                                        {
                                            label: 'Price',
                                            data: {
                                                property: 'timeseries',
                                                code: 'prices',
                                                index: -1,
                                                key: 'value',
                                                format: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    },
                                                },
                                            },
                                        },
                                        {
                                            label: 'Price Change',
                                            data: [
                                                {
                                                    property: 'timeseries',
                                                    code: 'prices',
                                                    index: 0,
                                                    key: 'value',
                                                    format: {
                                                        type: 'number',
                                                        options: {
                                                            style: 'decimal',
                                                            minimumFractionDigits: 1,
                                                            maximumFractionDigits: 1,
                                                        },
                                                    },
                                                    separator: ' / ',
                                                },
                                                {
                                                    property: 'timeseries',
                                                    code: 'prices',
                                                    index: -1,
                                                    key: 'value',
                                                    format: {
                                                        type: 'number',
                                                        options: {
                                                            style: 'decimal',
                                                            minimumFractionDigits: 1,
                                                            maximumFractionDigits: 1,
                                                        },
                                                    },
                                                    showChange: true,
                                                },
                                            ],
                                        },
                                    ],
                                },
                                {
                                    label: 'Ongoing Charge',
                                    data: {
                                        property: 'properties_pub',
                                        code: 'management_charge',
                                        key: 'value',
                                        format: {
                                            type: 'number',
                                            options: {
                                                style: 'percent',
                                            },
                                        },
                                    },
                                },
                                {
                                    type: 'prices',
                                    label: 'Historic Prices',
                                    data: {
                                        property: 'timeseries',
                                        code: 'prices',
                                        periodicity: 'MONTHLY',
                                    },
                                    sort: {
                                        key: 'date',
                                        type: 'date',
                                        direction: 'DESC',
                                    },
                                    columns: [
                                        {
                                            label: 'Fund Name',
                                            data: {
                                                property: 'properties_pub',
                                                code: 'fund_name',
                                                key: 'value',
                                            },
                                        },
                                        {
                                            label: 'ISIN',
                                            data: {
                                                property: 'properties_pub',
                                                code: 'isin',
                                                key: 'value',
                                            },
                                        },
                                        {
                                            label: 'Price Date',
                                            dataKey: 'date',
                                            format: {
                                                type: 'date',
                                                options: {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: '2-digit',
                                                },
                                            },
                                        },
                                        {
                                            label: 'Price',
                                            dataKey: 'value',
                                            format: {
                                                type: 'number',
                                                options: {
                                                    style: 'decimal',
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                },
                                            },
                                        },
                                    ],
                                    disclaimerTypes: ['Fees'],
                                    downloadFilename: {
                                        text: '{isin} {priceDate}',
                                        dataFields: {
                                            isin: {
                                                dataLocator: {
                                                    property: 'properties_pub',
                                                    code: 'isin',
                                                    key: 'value',
                                                },
                                            },
                                            priceDate: {
                                                dataLocator: {
                                                    property: 'timeseries',
                                                    code: 'prices',
                                                    index: -1,
                                                    key: 'date',
                                                    format: {
                                                        type: 'date',
                                                        options: {
                                                            day: '2-digit',
                                                            year: '2-digit',
                                                            month: '2-digit',
                                                        },
                                                    },
                                                },
                                            },
                                        },
                                    },
                                },
                                {
                                    type: 'document',
                                    label: 'Factsheet',
                                    documentType: 'Factsheet',
                                },
                                {
                                    type: 'document',
                                    label: 'KIID',
                                    documentType: 'KIID',
                                },
                                {
                                    type: 'watchlist',
                                    label: 'Watchlist',
                                    width: '100px',
                                    verticalAlign: 'bottom',
                                    showLabel: true,
                                },
                            ],
                            clearIndividualFilters: {
                                enabled: true,
                                align: 'right',
                            },
                            filterConfig: {
                                noDataDisplay: 'hide',
                                filters: [
                                    {
                                        type: 'CLSS',
                                        property: 'fund_name',
                                        label: 'Fund Name',
                                        ui: 'checklist',
                                        enableSearch: true,
                                    },
                                    {
                                        type: 'CLSS',
                                        property: 'ccy',
                                        label: 'Currency',
                                        ui: 'checklist',
                                        enableSearch: true,
                                    },
                                    {
                                        type: 'DOCUMENT_META',
                                        property: 'document_type',
                                        label: 'Document Type',
                                        ui: 'checklist',
                                        enableSearch: true,
                                        allowedValues: ['KIID', 'Factsheet'],
                                    },
                                    {
                                        type: 'DOCUMENT_PROPERTY',
                                        property: 'cultureCode',
                                        label: 'Document Language',
                                        ui: 'checklist',
                                        enableSearch: true,
                                        allowedValues: [
                                            'en-GB',
                                            'fr-FR',
                                            'it-IT',
                                            'de-DE',
                                        ],
                                    },
                                    {
                                        type: 'CLSS',
                                        property: 'non_existent',
                                        label: 'Should Not Render',
                                        ui: 'checklist',
                                        enableSearch: true,
                                    },
                                ],
                            },
                            documentSearch: [
                                {
                                    property: 'document_type',
                                    values: ['Factsheet', 'KIID'],
                                    matchtype: 'MATCH',
                                },
                            ],
                            cultureAliases: {
                                'en-GB': 'English',
                                'fr-FR': 'French',
                                'de-DE': 'German',
                                'it-IT': 'Italian',
                            },
                            disclaimerTypes: ['Fees', 'Past Perf'],
                        } as ModuleConfig,
                    },
                    {
                        id: 'library',
                        label: 'Library',
                        selected: false,
                        config: {
                            extendedInfo: {
                                // iconPosition: 'right',
                                options: {
                                    moduleConfig: {
                                        contentQuery: {
                                            queryOptionsType: 'none',
                                            value: 'Factsheet',
                                        },
                                        conditional: {
                                            conditions: [
                                                {
                                                    fieldQuery: {
                                                        queryOptionsType:
                                                            'document',
                                                        entityType: 'CLSS',
                                                        options: {
                                                            document: {
                                                                key: 'meta',
                                                                childKey:
                                                                    'document_type',
                                                            },
                                                        },
                                                    },
                                                    operator: '=',
                                                    value: 'Factsheet',
                                                },
                                            ],
                                        },
                                        iconFilename: 'minus.png',
                                        disabledIconFilename:
                                            'disabled_minus.png',
                                    },
                                    globalInfo: {
                                        contentQuery: {
                                            queryOptionsType: 'none',
                                            value:
                                                'This is an <b>OVERWRITTEN GLOBAL CONFIG</b> of the extended Info',
                                        },
                                        iconFilename: 'info.png',
                                        disabledIconFilename:
                                            'disabled_info.png',
                                    },
                                },
                            },
                            documentFilename: {
                                text:
                                    'library {document_type} {isin} @ {document_date}',
                                dataFields: {
                                    document_date: {
                                        property: 'document_date',
                                        format: {
                                            type: 'date',
                                            options: {
                                                day: '2-digit',
                                                year: '2-digit',
                                                month: '2-digit',
                                            },
                                        },
                                    },
                                    isin: {
                                        property: 'isin',
                                    },
                                    document_type: {
                                        property: 'document_type',
                                    },
                                },
                                options: {
                                    transforms: [
                                        {
                                            transformOptionsType: 'replace',
                                            replaceTransformOptions: {
                                                searchValue: '@',
                                                replaceValue: 'at',
                                            },
                                        },
                                    ],
                                },
                            },
                            fundGroupProperty: 'fund_name',
                            fundGroupHeader: {
                                actions: {
                                    dropdownOptions: {
                                        showBreakpoints: [
                                            {
                                                breakpoint: 'sm',
                                                operator: '>=',
                                            },
                                        ],
                                        labelOptions: {
                                            hide: true,
                                        },
                                    },
                                    disableExtendedInfo: true,
                                },
                            },
                            documentGroupMeta: 'document_type',
                            // documentGroupOrder: ['Factsheet', 'sadsd', 'KIID'],
                            documentGroupOrderMetaPropertySort: {
                                key: 'order',
                                type: 'number',
                                direction: 'ASC',
                            },
                            documentUngroupedLabel: 'Uncategorised',
                            showSingleDocument: false,
                            watchListEnabled: true,
                            watchListProperty: 'isin',
                            defaultCulture: 'en-GB',
                            selectedDocumentsLabel: {
                                singular: `{selected}/{total} Document selected`,
                                plural: `{selected}/{total} Documents selected`,
                            },
                            availableDocumentsLabel: {
                                singular: `{total} Document available`,
                                plural: `{total} Documents available`,
                            },
                            identifierSearch: {
                                properties: ['isin', 'fund_name'],
                                fuzzy: false,
                            },
                            searchLimit: 5,
                            autoSearchMinChar: 3,
                            sort: [
                                {
                                    key: 'fund_name',
                                    direction: 'ASC',
                                },
                                {
                                    key: 'isin',
                                    direction: 'ASC',
                                },
                            ],
                            documentsJoinSpec: [],
                            excludeExpiredDocuments: true,
                            links: {
                                salesforcePage: {
                                    url: 'https://google.com/search?q={search}',
                                    queries: [
                                        {
                                            queryId: 'search',
                                            queryOptionsType: 'none',
                                            value: 'pizza',
                                        },
                                    ],
                                    linkSegmentationFilter: {
                                        inclusionClauses: ['Test B'],
                                    },
                                },
                            },
                            columnConfig: [
                                {
                                    property: 'isin',
                                    label: 'ISIN',
                                    textAlign: 'left',
                                },
                                {
                                    property: 'class_code',
                                    label: 'Class Code',
                                    textAlign: 'left',
                                },
                                {
                                    property: 'ccy',
                                    label: 'Currency',
                                    textAlign: 'left',
                                },
                                {
                                    property: 'distribution_type',
                                    label: 'Distribution Type',
                                    textAlign: 'left',
                                },
                                {
                                    type: 'download',
                                    label: 'Actions',
                                    width: '400px',
                                    verticalAlign: 'bottom',
                                    actions: {
                                        disableExtendedInfo: true,
                                        displayDisabledIcons: true,
                                        dropdownOptions: {
                                            showBreakpoints: [
                                                {
                                                    breakpoint: 'sm',
                                                    operator: '<=',
                                                },
                                            ],
                                            labelOptions: {
                                                label: {
                                                    queryOptionsType: 'none',
                                                    value: 'Row Actions',
                                                },
                                                hide: false,
                                            },
                                            iconOptions: {
                                                expandedFilename: 'ExpandIcon',
                                                collapsedFilename:
                                                    'CollapseIcon',
                                            },
                                        },
                                        elements: [
                                            {
                                                type: 'link',
                                                labelOptions: {
                                                    label: {
                                                        queryOptionsType:
                                                            'none',
                                                        value:
                                                            'Salesforce Link',
                                                    },
                                                    disabledLabel: {
                                                        queryOptionsType:
                                                            'none',
                                                        value:
                                                            'Disabled Salesforce Link',
                                                    },
                                                    hideWhen: 'inline',
                                                },
                                                linkId: 'salesforcePage',
                                                iconOptions: {
                                                    filename: 'info.png',
                                                    disabledFilename:
                                                        'disabled_minus.png',
                                                },
                                            },
                                            {
                                                type: 'download',
                                                labelOptions: {
                                                    hideWhen: 'dropdown',
                                                },
                                                iconOptions: {
                                                    disabledFilename:
                                                        'disabled_minus.png',
                                                },
                                            },
                                            {
                                                type: 'copyUrl',
                                                labelOptions: {
                                                    label: {
                                                        queryOptionsType:
                                                            'none',
                                                        value:
                                                            'Copy Document URL(s)',
                                                    },
                                                    hideWhen: 'inline',
                                                },
                                                iconOptions: {
                                                    hide: false,
                                                    disabledFilename:
                                                        'disabled_minus.png',
                                                },
                                                url: {
                                                    queryOptionsType: 'none',
                                                    options: {
                                                        none: {
                                                            value:
                                                                'https://example.com/{sub:type}/{sub:culture}',
                                                            subQueries: [
                                                                {
                                                                    queryId:
                                                                        'type',
                                                                    queryOptionsType:
                                                                        'document',
                                                                    options: {
                                                                        document: {
                                                                            key:
                                                                                'meta',
                                                                            childKey:
                                                                                'document_type',
                                                                        },
                                                                    },
                                                                },
                                                                {
                                                                    queryId:
                                                                        'culture',
                                                                    queryOptionsType:
                                                                        'document',
                                                                    options: {
                                                                        document: {
                                                                            key:
                                                                                'cultureCode',
                                                                        },
                                                                    },
                                                                },
                                                            ],
                                                        },
                                                    },
                                                },
                                                feedback: {
                                                    feedbackMode: 'toaster',
                                                    textQuery: {
                                                        queryOptionsType:
                                                            'none',
                                                        options: {
                                                            none: {
                                                                value:
                                                                    'https://example.com/{sub:type}/{sub:culture}',
                                                                subQueries: [
                                                                    {
                                                                        queryId:
                                                                            'type',
                                                                        queryOptionsType:
                                                                            'document',
                                                                        options: {
                                                                            document: {
                                                                                key:
                                                                                    'meta',
                                                                                childKey:
                                                                                    'document_type',
                                                                            },
                                                                        },
                                                                    },
                                                                    {
                                                                        queryId:
                                                                            'culture',
                                                                        queryOptionsType:
                                                                            'document',
                                                                        options: {
                                                                            document: {
                                                                                key:
                                                                                    'cultureCode',
                                                                            },
                                                                        },
                                                                    },
                                                                ],
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                            {
                                                type: 'preview',
                                                labelOptions: {
                                                    hideWhen: 'inline',
                                                    label: {
                                                        queryOptionsType:
                                                            'none',
                                                        value:
                                                            'Preview {title}',
                                                    },
                                                },
                                                iconOptions: {
                                                    hide: false,
                                                    disabledFilename:
                                                        'disabled_minus.png',
                                                },
                                            },
                                        ],
                                    },
                                },
                            ],
                            rowConfig: {
                                type: 'Documents',
                                hideFilteredDocuments: true,
                                disableEmptyRow: false,
                                noDocumentsDisclaimers: [
                                    {
                                        disclaimerType: 'No Documents',
                                        linked: false,
                                    },
                                ],
                                documentCategories: [
                                    {
                                        heading: {
                                            queryOptionsType: 'none',
                                            value: 'Fund Pack',
                                        },
                                        groupBy: {
                                            queryOptionsType: 'documents',
                                            entityType: 'CLSS',
                                            collectionItemConditional: {
                                                conditions: [
                                                    {
                                                        value: 'Fund Pack',
                                                        operator: '=',
                                                        field:
                                                            'meta.document_category.value',
                                                    },
                                                ],
                                            },
                                        },
                                        showSingleDocument: true,
                                    },
                                    {
                                        heading: {
                                            queryOptionsType: 'none',
                                            value: 'Regulatory Pack',
                                        },
                                        groupBy: {
                                            queryOptionsType: 'documents',
                                            entityType: 'CLSS',
                                            collectionItemConditional: {
                                                conditions: [
                                                    {
                                                        value:
                                                            'Regulatory Pack',
                                                        operator: '=',
                                                        field:
                                                            'meta.document_category.value',
                                                    },
                                                ],
                                            },
                                        },
                                    },
                                    {
                                        heading: {
                                            queryOptionsType: 'none',
                                            value: 'Other',
                                        },
                                        groupBy: {
                                            queryOptionsType: 'documents',
                                            entityType: 'CLSS',
                                            collectionItemConditional: {
                                                conditions: [
                                                    {
                                                        value: [
                                                            'Fund Pack',
                                                            'Regulatory Pack',
                                                        ],
                                                        operator: 'not-in',
                                                        field:
                                                            'meta.document_category.value',
                                                    },
                                                ],
                                            },
                                        },
                                    },
                                ],
                                documentsGridConditionals: [
                                    {
                                        documentTypes: ['Factsheet'],
                                        config: {
                                            uiOptions: {
                                                dropdown: {
                                                    scrollIntoView: false,
                                                },
                                            },
                                            actions: {
                                                start: {
                                                    uiOptions: {
                                                        label: 'Select',
                                                        showLabel: false,
                                                        position: 'start',
                                                        width: '10%',
                                                        verticalAlign: 'top',
                                                        textAlign: 'left',
                                                        wrapText: false,
                                                    },
                                                    checkboxOptions: {
                                                        iconOptions: {
                                                            checkedFilename:
                                                                'CheckboxMarkedIconAlt',
                                                            uncheckedFilename:
                                                                'CheckboxIconAlt',
                                                        },
                                                    },
                                                    disableExtendedInfo: true,
                                                    elements: [],
                                                },
                                                end: {
                                                    uiOptions: {
                                                        label: 'Actions',
                                                        showLabel: true,
                                                        position: 'end',
                                                        width: '20%',
                                                        verticalAlign: 'top',
                                                        textAlign: 'right',
                                                        wrapText: false,
                                                    },
                                                    checkboxOptions: {
                                                        hide: true,
                                                    },
                                                    disableExtendedInfo: true,
                                                    elements: [
                                                        {
                                                            type: 'link',
                                                            labelOptions: {
                                                                label: {
                                                                    queryOptionsType:
                                                                        'none',
                                                                    value:
                                                                        'Salesforce Link',
                                                                },
                                                                disabledLabel: {
                                                                    queryOptionsType:
                                                                        'none',
                                                                    value:
                                                                        'Disabled Salesforce Link',
                                                                },
                                                                hideWhen:
                                                                    'inline',
                                                            },
                                                            linkId:
                                                                'salesforcePage',
                                                            iconOptions: {
                                                                filename:
                                                                    'info.png',
                                                                disabledFilename:
                                                                    'disabled_minus.png',
                                                            },
                                                        },
                                                        {
                                                            type: 'download',
                                                            labelOptions: {
                                                                label: {
                                                                    queryOptionsType:
                                                                        'none',
                                                                    value:
                                                                        'Download',
                                                                },
                                                                hide: true,
                                                            },
                                                            iconOptions: {
                                                                filename:
                                                                    'DownloadIconAlt',
                                                            },
                                                        },
                                                        {
                                                            type: 'preview',
                                                            labelOptions: {
                                                                label: {
                                                                    queryOptionsType:
                                                                        'none',
                                                                    value:
                                                                        'Preview doc {title}',
                                                                },
                                                                hide: true,
                                                            },
                                                            iconOptions: {
                                                                filename:
                                                                    'PreviewIconAlt',
                                                            },
                                                        },
                                                        {
                                                            type: 'copyUrl',
                                                            labelOptions: {
                                                                label: {
                                                                    queryOptionsType:
                                                                        'none',
                                                                    value:
                                                                        'Copy URL',
                                                                },
                                                                hide: true,
                                                            },
                                                            iconOptions: {
                                                                filename:
                                                                    'DownloadControlsCopyDocumentUrlIconAlt',
                                                                successFilename:
                                                                    'DownloadControlsCopyDocumentUrlSuccessIconAlt',
                                                            },
                                                            url: {
                                                                queryOptionsType:
                                                                    'none',
                                                                value:
                                                                    'https://www.google.com',
                                                            },
                                                        },
                                                    ],
                                                },
                                            },
                                            columns: [
                                                {
                                                    label: 'ISIN',
                                                    width: '10%',
                                                    textAlign: 'left',
                                                    verticalAlign: 'bottom',
                                                    wrapText: true,
                                                    query: {
                                                        queryOptionsType:
                                                            'documentEntity',
                                                        options: {
                                                            documentEntity: {
                                                                property: {
                                                                    documentEntityKey:
                                                                        'class',
                                                                    code:
                                                                        'isin',
                                                                },
                                                            },
                                                        },
                                                    },
                                                },
                                                {
                                                    label: 'Language',
                                                    width: '10%',
                                                    textAlign: 'left',
                                                    verticalAlign: 'bottom',
                                                    wrapText: true,
                                                    query: {
                                                        queryOptionsType:
                                                            'document',
                                                        entityType: 'FUND',
                                                        key: 'cultureCode',
                                                    },
                                                },
                                                {
                                                    label: 'Fund Name',
                                                    width: '10%',
                                                    textAlign: 'left',
                                                    verticalAlign: 'bottom',
                                                    wrapText: true,
                                                    query: {
                                                        queryOptionsType:
                                                            'documentEntity',
                                                        options: {
                                                            documentEntity: {
                                                                property: {
                                                                    documentEntityKey:
                                                                        'fund',
                                                                    code:
                                                                        'fund_name',
                                                                },
                                                            },
                                                        },
                                                    },
                                                },
                                            ],
                                            pagination: {
                                                pageSize: 10,
                                            },
                                            search: {
                                                placeholder: {
                                                    format: {
                                                        singular:
                                                            'Search {total} thing',
                                                        plural:
                                                            'Search all {total} things',
                                                    },
                                                    badge: {
                                                        show: true,
                                                        align: 'right',
                                                        format: {
                                                            singular:
                                                                '{total} share class',
                                                            plural:
                                                                '{total} share classes',
                                                        },
                                                    },
                                                },
                                            },
                                            caption: {
                                                text: 'Fund Documents',
                                                show: false,
                                            },
                                        },
                                    },
                                ],
                                documentSearchRequests: {
                                    inputs: [
                                        {
                                            key: 'fundClientCode',
                                            value: {
                                                queryOptionsType: 'property',
                                                entityType: 'CLSS',
                                                code: 'fund_code',
                                            },
                                        },
                                        {
                                            key: 'classIdentifier',
                                            value: {
                                                queryOptionsType: 'property',
                                                entityType: 'CLSS',
                                                code: 'isin',
                                            },
                                        },
                                    ],
                                    requests: [
                                        {
                                            type: 'FUND',
                                            search: [],
                                            include: {
                                                entity: {
                                                    joinSpecs: [
                                                        {
                                                            entityType: 'FUND',
                                                            joinFrom:
                                                                'fund_code',
                                                        },
                                                        {
                                                            entityType: 'CLSS',
                                                            joinFrom: 'isin',
                                                        },
                                                    ],
                                                },
                                            },
                                            searchEntityRequest: {
                                                type: 'CLSS',
                                                search: [
                                                    {
                                                        property: 'fund_code',
                                                        matchtype: 'MATCH',
                                                        values: [
                                                            '{fundClientCode}',
                                                        ],
                                                    },
                                                ],
                                            },
                                        },
                                        {
                                            type: 'CLSS',
                                            search: [
                                                {
                                                    property: 'entity_id',
                                                    matchtype: 'MATCH',
                                                    values: [
                                                        '{classIdentifier}',
                                                    ],
                                                },
                                            ],
                                            include: {
                                                entity: {
                                                    joinSpecs: [
                                                        {
                                                            entityType: 'FUND',
                                                            joinFrom:
                                                                'fund_code',
                                                        },
                                                        {
                                                            entityType: 'CLSS',
                                                            joinFrom:
                                                                'entity_id',
                                                        },
                                                    ],
                                                },
                                            },
                                            searchEntityRequest: {
                                                type: 'CLSS',
                                                fundList: '{app:fund_list}',
                                                search: [],
                                            },
                                        },
                                    ],
                                },
                            },
                            clearIndividualFilters: {
                                enabled: true,
                                align: 'right',
                            },
                            filterConfig: {
                                top: true,
                                clearIndividualFilters: {
                                    enabled: true,
                                    align: 'right',
                                },
                                shareableLink: {
                                    feedback: {
                                        feedbackMode: 'alert',
                                        alertOptions: {
                                            titleQuery: {
                                                queryOptionsType: 'none',
                                                value: 'Message in a bottle',
                                            },
                                        },
                                        textQuery: {
                                            queryOptionsType: 'none',
                                            value:
                                                'Successfully copied <a href="{sub:url}">url</a> to clipboard',
                                            subQueries: [
                                                {
                                                    queryId: 'url',
                                                    queryOptionsType:
                                                        'response',
                                                    options: {
                                                        response: {
                                                            key: 'url',
                                                        },
                                                    },
                                                },
                                            ],
                                        },
                                    },
                                    show: true,
                                    label: 'share',
                                },
                                filters: [
                                    {
                                        type: 'CLSS',
                                        property: 'fund_name',
                                        label: 'Fund Name',
                                        ui: 'checklist',
                                        enableSearch: true,
                                    },
                                    {
                                        type: 'CLSS',
                                        property: 'isin',
                                        label: 'ISIN',
                                        ui: 'checklist',
                                        enableSearch: true,
                                    },
                                    {
                                        type: 'CLSS',
                                        property: 'ccy',
                                        label: 'Currency',
                                        ui: 'checklist',
                                        enableSearch: true,
                                    },
                                    {
                                        type: 'CLSS',
                                        property: 'distribution_type',
                                        label: 'Distribution Type',
                                        ui: 'checklist',
                                        enableSearch: true,
                                    },
                                    {
                                        type: 'CLSS',
                                        property: 'class_code',
                                        label: 'Class Code',
                                        ui: 'checklist',
                                        enableSearch: true,
                                    },
                                    {
                                        type: 'DOCUMENT_META',
                                        property: 'document_type',
                                        label: 'Document Type',
                                        ui: 'checklist',
                                        enableSearch: true,
                                        allowedValues: ['KIID', 'Factsheet'],
                                        saveValueAsUserPreference: true,
                                    },
                                    {
                                        type: 'DOCUMENT_PROPERTY',
                                        property: 'cultureCode',
                                        label: 'Document Language',
                                        ui: 'checklist',
                                        enableSearch: true,
                                        allowedValues: [
                                            'en-GB',
                                            'fr-FR',
                                            'it-IT',
                                            'de-DE',
                                        ],
                                        saveValueAsUserPreference: true,
                                    },
                                ],
                            },
                            cultureAliases: {
                                'en-GB': 'English',
                                'fr-FR': 'French',
                                'de-DE': 'German',
                                'it-IT': 'Italian',
                            },
                            disclaimerTypes: ['Fees', 'Past Perf'],
                            downloadControlDefaults: {
                                // selectionState: 'unselected',
                                zeroCountOverride: '-',
                                hideZeroCount: false,
                            },
                        } as ModuleConfig,
                    },
                    {
                        id: 'documentGridColumns',
                        label: 'Document Grid Columns',
                        config: {
                            defaultCulture: 'en-GB',
                            searchLimit: 30,
                            cultureAliases: {
                                'en-GB': 'English',
                                'fr-FR': 'French',
                                'de-DE': 'German',
                                'it-IT': 'Italian',
                            },
                            documentsJoinSpec: [],
                            fundGroupProperty: 'fund_name',
                            fundGroupHeader: {
                                actions: {
                                    dropdownOptions: {
                                        showBreakpoints: [
                                            {
                                                breakpoint: 'sm',
                                                operator: '>=',
                                            },
                                        ],
                                        labelOptions: {
                                            hide: true,
                                        },
                                    },
                                    disableExtendedInfo: true,
                                },
                            },
                            documentsGrid: {
                                uiOptions: {
                                    dropdown: {
                                        scrollIntoView: false,
                                    },
                                },
                                actions: {
                                    start: {
                                        uiOptions: {
                                            label: 'Select',
                                            showLabel: false,
                                            position: 'start',
                                            width: '10%',
                                            verticalAlign: 'top',
                                            textAlign: 'left',
                                            wrapText: false,
                                        },
                                        checkboxOptions: {
                                            iconOptions: {
                                                checkedFilename:
                                                    'CheckboxMarkedIconAlt',
                                                uncheckedFilename:
                                                    'CheckboxIconAlt',
                                            },
                                        },
                                        disableExtendedInfo: true,
                                        elements: [],
                                    },
                                    end: {
                                        uiOptions: {
                                            label: 'Actions',
                                            showLabel: true,
                                            position: 'end',
                                            width: '20%',
                                            verticalAlign: 'top',
                                            textAlign: 'right',
                                            wrapText: false,
                                        },
                                        checkboxOptions: {
                                            hide: true,
                                        },
                                        disableExtendedInfo: true,
                                        elements: [
                                            {
                                                type: 'link',
                                                labelOptions: {
                                                    label: {
                                                        queryOptionsType:
                                                            'none',
                                                        value:
                                                            'Salesforce Link',
                                                    },
                                                    disabledLabel: {
                                                        queryOptionsType:
                                                            'none',
                                                        value:
                                                            'Disabled Salesforce Link',
                                                    },
                                                    hideWhen: 'inline',
                                                },
                                                linkId: 'salesforcePage',
                                                iconOptions: {
                                                    filename: 'info.png',
                                                    disabledFilename:
                                                        'disabled_minus.png',
                                                },
                                            },
                                            {
                                                type: 'download',
                                                labelOptions: {
                                                    label: {
                                                        queryOptionsType:
                                                            'none',
                                                        value: 'Download',
                                                    },
                                                    hide: true,
                                                },
                                                iconOptions: {
                                                    filename: 'DownloadIconAlt',
                                                },
                                            },
                                            {
                                                type: 'preview',
                                                labelOptions: {
                                                    label: {
                                                        queryOptionsType:
                                                            'none',
                                                        value:
                                                            'Preview doc {title}',
                                                    },
                                                    hide: true,
                                                },
                                                iconOptions: {
                                                    filename: 'PreviewIconAlt',
                                                },
                                            },
                                            {
                                                type: 'copyUrl',
                                                labelOptions: {
                                                    label: {
                                                        queryOptionsType:
                                                            'none',
                                                        value: 'Copy URL',
                                                    },
                                                    hide: true,
                                                },
                                                iconOptions: {
                                                    filename:
                                                        'DownloadControlsCopyDocumentUrlIconAlt',
                                                    successFilename:
                                                        'DownloadControlsCopyDocumentUrlSuccessIconAlt',
                                                },
                                                url: {
                                                    queryOptionsType: 'none',
                                                    value:
                                                        'https://www.google.com',
                                                },
                                            },
                                        ],
                                    },
                                },
                                columns: [
                                    {
                                        label: 'ISIN',
                                        width: '10%',
                                        textAlign: 'left',
                                        verticalAlign: 'bottom',
                                        wrapText: true,
                                        query: {
                                            queryOptionsType: 'documentEntity',
                                            options: {
                                                documentEntity: {
                                                    property: {
                                                        documentEntityKey:
                                                            'class',
                                                        code: 'isin',
                                                    },
                                                },
                                            },
                                        },
                                    },
                                    {
                                        label: 'Language',
                                        width: '10%',
                                        textAlign: 'left',
                                        verticalAlign: 'bottom',
                                        wrapText: true,
                                        query: {
                                            queryOptionsType: 'document',
                                            entityType: 'FUND',
                                            key: 'cultureCode',
                                        },
                                    },
                                    {
                                        label: 'Fund Name',
                                        width: '10%',
                                        textAlign: 'left',
                                        verticalAlign: 'bottom',
                                        wrapText: true,
                                        query: {
                                            queryOptionsType: 'documentEntity',
                                            options: {
                                                documentEntity: {
                                                    property: {
                                                        documentEntityKey:
                                                            'fund',
                                                        code: 'fund_name',
                                                    },
                                                },
                                            },
                                        },
                                    },
                                ],
                                pagination: {
                                    pageSize: 10,
                                },
                                search: {
                                    placeholder: {
                                        format: {
                                            singular: 'Search {total} thing',
                                            plural: 'Search all {total} things',
                                        },
                                        badge: {
                                            show: true,
                                            align: 'right',
                                            format: {
                                                singular: '{total} share class',
                                                plural: '{total} share classes',
                                            },
                                        },
                                    },
                                },
                                caption: {
                                    text: 'Fund Documents',
                                    show: false,
                                },
                                noDataDisclaimerTypes: [
                                    {
                                        disclaimerType: 'Fees',
                                        linked: false,
                                    },
                                ],
                            },
                            columnConfig: [
                                {
                                    label: 'Fund',
                                    data: [
                                        {
                                            property: 'properties_pub',
                                            code: 'short_name',
                                            key: 'value',
                                            sortable: false,
                                        },
                                    ],
                                },
                                {
                                    type: 'document',
                                    label: 'Factsheet',
                                    columnDocumentsGrid: {
                                        id: 'factsheet',
                                        documentSearchRequests: {
                                            inputs: [
                                                {
                                                    key: 'classIdentifier',
                                                    value: {
                                                        queryOptionsType:
                                                            'property',
                                                        entityType: 'CLSS',
                                                        code: 'isin',
                                                    },
                                                },
                                            ],
                                            requests: [
                                                {
                                                    type: 'CLSS',
                                                    search: [
                                                        {
                                                            property: 'isin',
                                                            matchtype: 'MATCH',
                                                            values: [
                                                                '{classIdentifier}',
                                                            ],
                                                        },
                                                        {
                                                            property:
                                                                'document_type',
                                                            matchtype: 'MATCH',
                                                            values: [
                                                                'Factsheet',
                                                            ],
                                                        },
                                                    ],
                                                    include: {
                                                        entity: {
                                                            joinSpecs: [
                                                                {
                                                                    entityType:
                                                                        'CLSS',
                                                                    joinFrom:
                                                                        'isin',
                                                                },
                                                            ],
                                                        },
                                                    },
                                                    excludeExpired: true,
                                                },
                                            ],
                                        },
                                    },
                                },
                                {
                                    type: 'document',
                                    label: 'KIID',
                                    columnDocumentsGrid: {
                                        id: 'kiid - override',
                                        documentsGrid: {
                                            uiOptions: {
                                                dropdown: {
                                                    scrollIntoView: false,
                                                },
                                            },
                                            actions: {
                                                start: {
                                                    uiOptions: {
                                                        label: 'Select',
                                                        showLabel: false,
                                                        position: 'start',
                                                        width: '10%',
                                                        verticalAlign: 'top',
                                                        textAlign: 'left',
                                                        wrapText: false,
                                                    },
                                                    checkboxOptions: {
                                                        iconOptions: {
                                                            checkedFilename:
                                                                'CheckboxMarkedIconAlt',
                                                            uncheckedFilename:
                                                                'CheckboxIconAlt',
                                                        },
                                                    },
                                                    disableExtendedInfo: true,
                                                    elements: [],
                                                },
                                                end: {
                                                    uiOptions: {
                                                        label: 'Actions',
                                                        showLabel: true,
                                                        position: 'end',
                                                        width: '20%',
                                                        verticalAlign: 'top',
                                                        textAlign: 'right',
                                                        wrapText: false,
                                                    },
                                                    checkboxOptions: {
                                                        hide: true,
                                                    },
                                                    disableExtendedInfo: true,
                                                    elements: [
                                                        {
                                                            type: 'link',
                                                            labelOptions: {
                                                                label: {
                                                                    queryOptionsType:
                                                                        'none',
                                                                    value:
                                                                        'Salesforce Link',
                                                                },
                                                                disabledLabel: {
                                                                    queryOptionsType:
                                                                        'none',
                                                                    value:
                                                                        'Disabled Salesforce Link',
                                                                },
                                                                hideWhen:
                                                                    'inline',
                                                            },
                                                            linkId:
                                                                'salesforcePage',
                                                            iconOptions: {
                                                                filename:
                                                                    'info.png',
                                                                disabledFilename:
                                                                    'disabled_minus.png',
                                                            },
                                                        },
                                                        {
                                                            type: 'download',
                                                            labelOptions: {
                                                                label: {
                                                                    queryOptionsType:
                                                                        'none',
                                                                    value:
                                                                        'Download',
                                                                },
                                                                hide: true,
                                                            },
                                                            iconOptions: {
                                                                filename:
                                                                    'DownloadIconAlt',
                                                            },
                                                        },
                                                        {
                                                            type: 'preview',
                                                            labelOptions: {
                                                                label: {
                                                                    queryOptionsType:
                                                                        'none',
                                                                    value:
                                                                        'Preview doc {title}',
                                                                },
                                                                hide: true,
                                                            },
                                                            iconOptions: {
                                                                filename:
                                                                    'PreviewIconAlt',
                                                            },
                                                        },
                                                        {
                                                            type: 'copyUrl',
                                                            labelOptions: {
                                                                label: {
                                                                    queryOptionsType:
                                                                        'none',
                                                                    value:
                                                                        'Copy URL',
                                                                },
                                                                hide: true,
                                                            },
                                                            iconOptions: {
                                                                filename:
                                                                    'DownloadControlsCopyDocumentUrlIconAlt',
                                                                successFilename:
                                                                    'DownloadControlsCopyDocumentUrlSuccessIconAlt',
                                                            },
                                                            url: {
                                                                queryOptionsType:
                                                                    'none',
                                                                value:
                                                                    'https://www.google.com',
                                                            },
                                                        },
                                                    ],
                                                },
                                            },
                                            columns: [
                                                {
                                                    label: 'ISIN - override',
                                                    width: '15%',
                                                    textAlign: 'left',
                                                    verticalAlign: 'bottom',
                                                    wrapText: true,
                                                    query: {
                                                        queryOptionsType:
                                                            'documentEntity',
                                                        options: {
                                                            documentEntity: {
                                                                property: {
                                                                    documentEntityKey:
                                                                        'class',
                                                                    code:
                                                                        'isin',
                                                                },
                                                            },
                                                        },
                                                    },
                                                },
                                                {
                                                    label:
                                                        'Language - override',
                                                    width: '20%',
                                                    textAlign: 'left',
                                                    verticalAlign: 'bottom',
                                                    wrapText: true,
                                                    query: {
                                                        queryOptionsType:
                                                            'document',
                                                        entityType: 'FUND',
                                                        key: 'cultureCode',
                                                    },
                                                },
                                                {
                                                    label:
                                                        'Fund Name - override',
                                                    width: '10%',
                                                    textAlign: 'left',
                                                    verticalAlign: 'bottom',
                                                    wrapText: true,
                                                    query: {
                                                        queryOptionsType:
                                                            'documentEntity',
                                                        options: {
                                                            documentEntity: {
                                                                property: {
                                                                    documentEntityKey:
                                                                        'fund',
                                                                    code:
                                                                        'fund_name',
                                                                },
                                                            },
                                                        },
                                                    },
                                                },
                                            ],
                                            pagination: {
                                                pageSize: 10,
                                            },
                                            search: {
                                                placeholder: {
                                                    format: {
                                                        singular:
                                                            'Search {total} thing',
                                                        plural:
                                                            'Search all {total} things',
                                                    },
                                                    badge: {
                                                        show: true,
                                                        align: 'right',
                                                        format: {
                                                            singular:
                                                                '{total} share class',
                                                            plural:
                                                                '{total} share classes',
                                                        },
                                                    },
                                                },
                                            },
                                            caption: {
                                                text: 'Fund Documents',
                                                show: false,
                                            },
                                        },
                                        documentSearchRequests: {
                                            inputs: [
                                                {
                                                    key: 'classIdentifier',
                                                    value: {
                                                        queryOptionsType:
                                                            'property',
                                                        entityType: 'CLSS',
                                                        code: 'isin',
                                                    },
                                                },
                                            ],
                                            requests: [
                                                {
                                                    type: 'CLSS',
                                                    search: [
                                                        {
                                                            property: 'isin',
                                                            matchtype: 'MATCH',
                                                            values: [
                                                                '{classIdentifier}',
                                                            ],
                                                        },
                                                        {
                                                            property:
                                                                'document_type',
                                                            matchtype: 'MATCH',
                                                            values: ['KIID'],
                                                        },
                                                    ],
                                                    include: {
                                                        entity: {
                                                            joinSpecs: [
                                                                {
                                                                    entityType:
                                                                        'CLSS',
                                                                    joinFrom:
                                                                        'isin',
                                                                },
                                                            ],
                                                        },
                                                    },
                                                },
                                            ],
                                        },
                                    },
                                },
                                {
                                    type: 'document',
                                    label: 'Prospectus',
                                    columnDocumentsGrid: {
                                        id: 'prospectus',
                                        documentSearchRequests: {
                                            inputs: [
                                                {
                                                    key: 'classIdentifier',
                                                    value: {
                                                        queryOptionsType:
                                                            'property',
                                                        entityType: 'CLSS',
                                                        code: 'isin',
                                                    },
                                                },
                                            ],
                                            requests: [
                                                {
                                                    type: 'CLSS',
                                                    search: [
                                                        {
                                                            property: 'isin',
                                                            matchtype: 'MATCH',
                                                            values: [
                                                                '{classIdentifier}',
                                                            ],
                                                        },
                                                        {
                                                            property:
                                                                'document_type',
                                                            matchtype: 'MATCH',
                                                            values: [
                                                                'Prospectus',
                                                            ],
                                                        },
                                                    ],
                                                    include: {
                                                        entity: {
                                                            joinSpecs: [
                                                                {
                                                                    entityType:
                                                                        'CLSS',
                                                                    joinFrom:
                                                                        'isin',
                                                                },
                                                            ],
                                                        },
                                                    },
                                                },
                                            ],
                                        },
                                    },
                                },
                                {
                                    type: 'document',
                                    label: 'Marketing',
                                    columnDocumentsGrid: {
                                        id: 'marketing',
                                        documentSearchRequests: {
                                            inputs: [
                                                {
                                                    key: 'classIdentifier',
                                                    value: {
                                                        queryOptionsType:
                                                            'property',
                                                        entityType: 'CLSS',
                                                        code: 'isin',
                                                    },
                                                },
                                            ],
                                            requests: [
                                                {
                                                    type: 'CLSS',
                                                    search: [
                                                        {
                                                            property: 'isin',
                                                            matchtype: 'MATCH',
                                                            values: [
                                                                '{classIdentifier}',
                                                            ],
                                                        },
                                                        {
                                                            property:
                                                                'document_type',
                                                            matchtype: 'MATCH',
                                                            values: [
                                                                'Marketing',
                                                            ],
                                                        },
                                                    ],
                                                    include: {
                                                        entity: {
                                                            joinSpecs: [
                                                                {
                                                                    entityType:
                                                                        'CLSS',
                                                                    joinFrom:
                                                                        'isin',
                                                                },
                                                            ],
                                                        },
                                                    },
                                                },
                                            ],
                                        },
                                    },
                                },
                                {
                                    type: 'download',
                                    label: 'Actions',
                                    width: '400px',
                                    verticalAlign: 'bottom',
                                    actions: {
                                        dropdownOptions: {
                                            showBreakpoints: [
                                                {
                                                    breakpoint: 'sm',
                                                    operator: '<=',
                                                },
                                            ],
                                            labelOptions: {
                                                label: {
                                                    queryOptionsType: 'none',
                                                    value: 'Row Actions',
                                                },
                                                hide: false,
                                            },
                                            iconOptions: {
                                                expandedFilename: 'ExpandIcon',
                                                collapsedFilename:
                                                    'CollapseIcon',
                                            },
                                        },
                                        elements: [
                                            {
                                                type: 'link',
                                                labelOptions: {
                                                    label: {
                                                        queryOptionsType:
                                                            'none',
                                                        value:
                                                            'Salesforce Link',
                                                    },
                                                    disabledLabel: {
                                                        queryOptionsType:
                                                            'none',
                                                        value:
                                                            'Disabled Salesforce Link',
                                                    },
                                                    hideWhen: 'inline',
                                                },
                                                linkId: 'salesforcePage',
                                                iconOptions: {
                                                    filename: 'info.png',
                                                    disabledFilename:
                                                        'disabled_minus.png',
                                                },
                                            },
                                            {
                                                type: 'download',
                                                labelOptions: {
                                                    hideWhen: 'dropdown',
                                                },
                                                iconOptions: {
                                                    disabledFilename:
                                                        'disabled_minus.png',
                                                },
                                            },
                                        ],
                                    },
                                },
                            ],
                            downloadControlDefaults: {
                                selectionState: 'unselected',
                                hideZeroCount: false,
                            },
                        } as ModuleConfig,
                    },
                    {
                        id: 'document',
                        type: 'DocumentList',
                        label: 'Documents',
                        config: {
                            showSingleDocument: true,
                            documentFilename: {
                                text:
                                    'document {document_type} {isin} {document_date}',
                                dataFields: {
                                    document_date: {
                                        property: 'document_date',
                                        format: {
                                            type: 'date',
                                            options: {
                                                day: '2-digit',
                                                year: '2-digit',
                                                month: '2-digit',
                                            },
                                        },
                                    },
                                    isin: {
                                        property: 'isin',
                                    },
                                    document_type: {
                                        property: 'document_type',
                                    },
                                },
                            },
                            documentDisplayName: {
                                text: '{title} {document_date} {culture}',
                                dataFields: {
                                    document_date: {
                                        property: 'document_date',
                                        format: {
                                            type: 'date',
                                            options: {
                                                day: '2-digit',
                                                year: '2-digit',
                                                month: '2-digit',
                                            },
                                        },
                                    },
                                    title: {
                                        property: 'title',
                                    },
                                    culture: {
                                        property: 'cultureCode',
                                        globalValueAliases: 'cultures',
                                    },
                                },
                            },
                            header: {
                                label: 'Document Library',
                            },
                            grouping: {
                                meta: 'document_type',
                                // property: 'title',
                                // order: ['Other', 'KIID', 'Factsheet'],
                                orderMetaPropertySort: {
                                    key: 'order',
                                    type: 'number',
                                    direction: 'ASC',
                                },
                                ungroupedLabel: 'Uncategorised',
                            },
                            documentCategories: [
                                {
                                    header: 'Fund Pack',
                                    groupBy: {
                                        conditions: [
                                            {
                                                value: 'Fund Pack',
                                                operator: '=',
                                                field:
                                                    'meta.document_category.value',
                                            },
                                        ],
                                    },
                                    showSingleDocument: false,
                                },
                                {
                                    header: 'Other',
                                    groupBy: {
                                        conditions: [
                                            {
                                                value: [
                                                    'Fund Pack',
                                                    'Regulatory Pack',
                                                ],
                                                operator: 'not-in',
                                                field:
                                                    'meta.document_category.value',
                                            },
                                        ],
                                    },
                                },
                            ],
                            identifierSearch: {
                                properties: ['document_type', 'isin'],
                                fuzzy: true,
                                placeholder: {
                                    format: {
                                        singular: 'Search by {properties}',
                                        plural: 'Search by {properties}',
                                    },
                                    aliases: [
                                        {
                                            key: 'document_type',
                                            value: 'Document Type',
                                        },
                                    ],
                                },
                            },
                            watchListEnabled: true,
                            watchListProperty: 'isin',
                            filtersTop: true,
                            filters: [
                                {
                                    type: 'CLSS',
                                    property: 'fund_name',
                                    label: 'Fund Name',
                                    ui: 'checklist',
                                    enableSearch: true,
                                    disclaimers: [
                                        {
                                            disclaimerType: 'Fees',
                                            display: 'tooltip',
                                        },
                                    ],
                                },
                                {
                                    type: 'CLSS',
                                    property: 'isin',
                                    label: 'ISIN',
                                    ui: 'checklist',
                                    enableSearch: true,
                                },
                                {
                                    type: 'DOCUMENT_META',
                                    property: 'document_type',
                                    label: 'Document Type',
                                    ui: 'checklist',
                                    enableSearch: true,
                                    allowedValues: ['KIID'],
                                },
                                {
                                    type: 'DOCUMENT_PROPERTY',
                                    property: 'cultureCode',
                                    label: 'Document Language',
                                    ui: 'checklist',
                                    enableSearch: true,
                                    allowedValues: [
                                        'en-GB',
                                        'fr-FR',
                                        'it-IT',
                                        'de-DE',
                                    ],
                                },
                            ],
                            search: {
                                config: {
                                    type: 'CLSS',
                                    search: [
                                        {
                                            property: 'document_type',
                                            values: [
                                                'Factsheet',
                                                'KIID',
                                                'Marketing',
                                                'Other',
                                                'Prospectus',
                                            ],
                                            matchtype: 'MATCH',
                                        },
                                    ],
                                    sort: {
                                        key: 'document_type',
                                        direction: 'ASC',
                                    },
                                    start: 0,
                                    autoSearchMinChar: 3,
                                    excludeExpired: true,
                                },
                                segmentationSearch: {
                                    All: [
                                        {
                                            property: 'document_type',
                                            values: ['Other'],
                                            matchtype: 'MATCH',
                                        },
                                    ],
                                },
                            },
                            defaultCulture: 'en-GB',
                            cultureAliases: {
                                'en-GB': 'English',
                                'fr-FR': 'French',
                                'de-DE': 'German',
                                'it-IT': 'Italian',
                            },
                            disclaimers: {
                                header: ['Fees'],
                                footer: ['Past Perf'],
                            },
                        } as DocumentModuleConfig,
                    },
                ],
            },
            {
                fundType: 'BOND',
                label: 'Bond',
                showModules: true,
                disclaimers: [
                    {
                        disclaimerType: 'Past Perf',
                        display: DisclaimerDisplayType.superscript,
                    },
                ],
                moduleMobileSelector: 'dropdown',
                modules: [
                    {
                        id: 'dailyNAV',
                        label: 'Bond Daily NAV',
                        config: {
                            fundGroupProperty: 'fund_name',
                            documentGroupMeta: 'document_type',
                            watchListEnabled: true,
                            watchListProperty: 'isin',
                            defaultCulture: 'en-GB',
                            identifierSearch: {
                                properties: ['isin', 'fund_name'],
                                fuzzy: false,
                            },
                            searchLimit: 30,
                            autoSearchMinChar: 3,
                            sort: [
                                {
                                    key: 'fund_name',
                                    direction: 'ASC',
                                },
                                {
                                    key: 'isin',
                                    direction: 'ASC',
                                },
                            ],
                            documentsJoinSpec: [
                                {
                                    joinTo: 'isin',
                                    joinFrom: 'isin',
                                },
                                {
                                    joinTo: 'fund_name',
                                    joinFrom: 'fund_name',
                                },
                            ],
                            columnConfig: [
                                {
                                    property: 'isin',
                                    label: 'ISIN',
                                },
                                {
                                    property: 'class_code',
                                    label: 'Class Code',
                                },
                                {
                                    property: 'ccy',
                                    label: 'Currency',
                                },
                                {
                                    property: 'distribution_type',
                                    label: 'Distribution Type',
                                },
                            ],
                            rowConfig: {
                                type: 'FundCard',
                                header: {
                                    nameProperty: 'public_share_class_name',
                                    linkProperty: 'long_name',
                                    linkLabel: 'Fund Page',
                                },
                                fundCommentary: {
                                    label: 'Investment Objective',
                                    commentaries: [
                                        {
                                            commentaryType: 'Fund Commentary',
                                        },
                                    ],
                                },
                                fundFacts: {
                                    label: 'Fund Facts',
                                    cards: [
                                        {
                                            label: 'Price (NAV)',
                                            primary: true,
                                            data: {
                                                property: 'timeseries',
                                                code: 'prices',
                                                index: -1,
                                                key: 'value',
                                                format: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    },
                                                },
                                            },
                                            asAtDate: {
                                                label: 'As at',
                                                data: {
                                                    property: 'timeseries',
                                                    code: 'returns',
                                                    ccy: 'NA',
                                                    key: 'date',
                                                    index: 0,
                                                    format: {
                                                        type: 'date',
                                                        options: {
                                                            year: '2-digit',
                                                            month: 'short',
                                                            day: '2-digit',
                                                        },
                                                    },
                                                },
                                            },
                                            disclaimer: {
                                                disclaimerType: 'KIID Charges',
                                                linked: true,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'Bid Price',
                                            primary: true,
                                            data: {
                                                property: 'properties_pub',
                                                code: 'bid_price',
                                                noDataPlaceholder: 'n/a',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: true,
                                            },
                                            asAtDate: {
                                                label: 'As at',
                                                data: {
                                                    property: 'properties_pub',
                                                    code: 'fund_launch_date',
                                                    key: 'value',
                                                },
                                            },
                                        },
                                        {
                                            label: 'Offer Price',
                                            primary: true,
                                            data: {
                                                property: 'properties_pub',
                                                code: 'offer_price',
                                                noDataPlaceholder: 'n/a',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'Price Change',
                                            primary: true,
                                            data: [
                                                {
                                                    property: 'timeseries',
                                                    code: 'prices',
                                                    index: 0,
                                                    key: 'value',
                                                    format: {
                                                        type: 'number',
                                                        options: {
                                                            style: 'decimal',
                                                            minimumFractionDigits: 1,
                                                            maximumFractionDigits: 1,
                                                        },
                                                    },
                                                    separator: ' / ',
                                                },
                                                {
                                                    property: 'timeseries',
                                                    code: 'prices',
                                                    index: -1,
                                                    key: 'value',
                                                    format: {
                                                        type: 'number',
                                                        options: {
                                                            style: 'decimal',
                                                            minimumFractionDigits: 1,
                                                            maximumFractionDigits: 1,
                                                        },
                                                    },
                                                    showChange: true,
                                                },
                                            ],
                                        },
                                        {
                                            label: 'SRRI',
                                            higherRiskLabel: 'High',
                                            lowerRiskLabel: 'Low',
                                            primary: true,
                                            ui: 'srri',
                                            data: {
                                                property: 'statistics',
                                                code: 'srri',
                                                index: 0,
                                                key: 'value',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'KIID Risks',
                                                linked: true,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'Morningstar Rating',
                                            primary: true,
                                            ui: 'stars',
                                            data: {
                                                property: 'statistics',
                                                code: 'srri',
                                                index: 0,
                                                key: 'value',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'KIID Risks',
                                                linked: true,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'AUM',
                                            primary: true,
                                            property: 'aum',
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: false,
                                            },
                                        },
                                        {
                                            label: 'Ongoing Charge',
                                            primary: true,
                                            data: {
                                                property: 'properties_pub',
                                                code: 'management_charge',
                                                key: 'value',
                                                format: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    },
                                                },
                                            },
                                        },
                                        {
                                            label: 'Yield',
                                            property: 'yield',
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'SRRI',
                                            ui: 'srri',
                                            data: {
                                                property: 'statistics',
                                                code: 'srri',
                                                index: 0,
                                                key: 'value',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'KIID Risks',
                                                linked: true,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'AUM',
                                            property: 'aum',
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: false,
                                            },
                                            asAtDate: {
                                                label: 'As at',
                                                data: {
                                                    property: 'properties_pub',
                                                    code: 'fund_launch_date',
                                                    key: 'value',
                                                },
                                            },
                                        },
                                        {
                                            label: 'Ongoing Charge',
                                            data: {
                                                property: 'properties_pub',
                                                code: 'management_charge',
                                                key: 'value',
                                                format: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    },
                                                },
                                            },
                                        },
                                        {
                                            label: 'Pay Dates',
                                            data: [
                                                {
                                                    property: 'properties_pub',
                                                    code: 'inception_date',
                                                    key: 'value',
                                                    separator: ', ',
                                                },
                                                {
                                                    property: 'properties_pub',
                                                    code: 'launch_date',
                                                    key: 'value',
                                                    separator: ', ',
                                                },
                                                {
                                                    property: 'properties_pub',
                                                    code: 'inception_date',
                                                    key: 'value',
                                                    separator: ', ',
                                                },
                                                {
                                                    property: 'properties_pub',
                                                    code: 'launch_date',
                                                    key: 'value',
                                                },
                                            ],
                                        },
                                    ],
                                    disclaimers: [
                                        {
                                            disclaimerType: 'Fees',
                                            linked: false,
                                        },
                                        {
                                            disclaimerType: 'KIID Charges',
                                            linked: true,
                                        },
                                    ],
                                },
                                fundManagers: {
                                    label: 'Fund Managers',
                                    showImage: true,
                                    showBiography: true,
                                    showExtended: [
                                        {
                                            property: 'title',
                                        },
                                    ],
                                    link: {
                                        label: 'Read Bio',
                                        property: 'bio_link',
                                    },
                                    autoPlay: 3000,
                                },
                                charts: [
                                    {
                                        id: 'cumulativePerformance',
                                        label: 'Cumulative Performance',
                                        selected: false,
                                        grouped: true,
                                        type: 'LineChart',
                                        data: {
                                            property: 'timeseries',
                                            code: 'prices',
                                            ccy: 'NA',
                                            classification: 'NA',
                                            periodicity: 'MONTHLY',
                                        },
                                        config: {
                                            legend: true,
                                            grid: {
                                                stroke: 'gray300',
                                                vertical: false,
                                            },
                                            tooltip: {
                                                labelFormat: {
                                                    type: 'date',
                                                    options: {
                                                        year: '2-digit',
                                                        month: 'short',
                                                    },
                                                },
                                                valueFormat: {
                                                    type: 'currency',
                                                    options: {
                                                        style: 'currency',
                                                        currency: 'GBP',
                                                        currencyDisplay: 'code',
                                                        minimumFractionDigits: 1,
                                                        maximumFractionDigits: 2,
                                                    },
                                                },
                                            },
                                            xAxis: {
                                                dataKey: 'date',
                                                format: {
                                                    type: 'date',
                                                    options: {
                                                        year: '2-digit',
                                                        month: 'short',
                                                    },
                                                },
                                            },
                                            yAxis: {
                                                label: {
                                                    value: 'Price',
                                                    angle: -90,
                                                    position: 'insideLeft',
                                                },
                                                dataKey: 'value',
                                            },
                                            series: [
                                                {
                                                    label: 'Fund',
                                                    dataKey: 'value',
                                                    color: 'primary',
                                                },
                                            ],
                                        },
                                        asAtDate: {
                                            label: 'As at',
                                            data: {
                                                property: 'timeseries',
                                                code: 'returns',
                                                ccy: 'NA',
                                                key: 'date',
                                                index: 0,
                                            },
                                            format: {
                                                type: 'date',
                                                options: {
                                                    year: '2-digit',
                                                    month: 'short',
                                                    day: '2-digit',
                                                },
                                            },
                                        },
                                        noDataDisclaimerTypes: [
                                            {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                            },
                                        ],
                                        disclaimerTypes: [
                                            {
                                                disclaimerType: 'KIID Charges',
                                                linked: true,
                                            },
                                        ],
                                    },
                                    {
                                        id: 'annualisedPerformance',
                                        label: 'Annualised Performance',
                                        selected: true,
                                        grouped: true,
                                        type: 'BarChart',
                                        data: {
                                            property: 'statistics',
                                            code: 'performance_annual_returns',
                                            ccy: 'NA',
                                        },
                                        config: {
                                            legend: true,
                                            grid: false,
                                            tooltip: {
                                                valueFormat: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 1,
                                                    },
                                                },
                                            },
                                            label: {
                                                precision: 2,
                                                spacing: 5,
                                            },
                                            xAxis: {
                                                dataKey: 'label',
                                                type: 'category',
                                            },
                                            yAxis: {
                                                label: {
                                                    value: 'Percentage %',
                                                    angle: -90,
                                                    position: 'insideLeft',
                                                },
                                                type: 'number',
                                            },
                                            series: [
                                                {
                                                    name: 'Fund',
                                                    nameProperty: 'fund_name',
                                                    dataKey: 'value',
                                                    fill: 'primary',
                                                    unit: '%',
                                                },
                                                {
                                                    name: 'Benchmark',
                                                    dataKey: 'value',
                                                    fill: 'gray500',
                                                    unit: '%',
                                                },
                                            ],
                                        },
                                        noDataDisclaimerTypes: [
                                            {
                                                disclaimerType: 'KIID Charges',
                                                linked: true,
                                            },
                                            {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                            },
                                        ],
                                        disclaimerTypes: [
                                            {
                                                disclaimerType:
                                                    'KIID Past Perf',
                                                linked: true,
                                            },
                                            {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                            },
                                        ],
                                    },
                                    {
                                        id: 'cumulativePerformance2',
                                        label: 'Cumulative Performance 2',
                                        selected: false,
                                        grouped: false,
                                        type: 'LineChart',
                                        data: {
                                            property: 'timeseries',
                                            code: 'returns',
                                            ccy: 'NA',
                                            classification: 'NA',
                                            periodicity: 'MONTHLY',
                                        },
                                        config: {
                                            legend: true,
                                            grid: {
                                                vertical: false,
                                            },
                                            tooltip: {
                                                labelFormat: {
                                                    type: 'date',
                                                    options: {
                                                        day: '2-digit',
                                                        month: 'long',
                                                        year: 'numeric',
                                                    },
                                                },
                                                valueFormat: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 3,
                                                    },
                                                },
                                            },
                                            xAxis: {
                                                dataKey: 'date',
                                                format: {
                                                    type: 'date',
                                                    options: {
                                                        year: '2-digit',
                                                        month: 'short',
                                                    },
                                                },
                                            },
                                            yAxis: {
                                                format: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 3,
                                                    },
                                                },
                                            },
                                            series: [
                                                {
                                                    dataKey: 'benchmarkReturn',
                                                    color: 'primary',
                                                },
                                                {
                                                    dataKey: 'benchmarkReturn2',
                                                    color: 'secondary',
                                                    connectNulls: true,
                                                },
                                            ],
                                        },
                                        noDataDisclaimerTypes: [
                                            'No Chart Data',
                                        ],
                                        disclaimerTypes: ['Past Perf'],
                                    },
                                ],
                                keyDocuments: {
                                    label: 'Key Documents',
                                    documentTypes: ['Factsheet', 'XXX', 'KIID'],
                                    linkProperty: 'long_name',
                                    linkLabel: 'View all documents',
                                },
                                footnoteScheme: 'numeric',
                                disclaimers: [
                                    {
                                        disclaimerType:
                                            'KIID Practical Information',
                                        linked: true,
                                    },
                                ],
                            },
                            clearIndividualFilters: {
                                enabled: true,
                                align: 'right',
                            },
                            filters: [
                                {
                                    type: 'CLSS',
                                    property: 'fund_name',
                                    label: 'Fund Name',
                                    ui: 'checklist',
                                    enableSearch: true,
                                },
                                {
                                    type: 'CLSS',
                                    property: 'isin',
                                    label: 'ISIN',
                                    ui: 'checklist',
                                    enableSearch: true,
                                },
                                {
                                    type: 'CLSS',
                                    property: 'ccy',
                                    label: 'Currency',
                                    ui: 'checklist',
                                    enableSearch: true,
                                },
                                {
                                    type: 'CLSS',
                                    property: 'distribution_type',
                                    label: 'Distribution Type',
                                    ui: 'checklist',
                                    enableSearch: true,
                                },
                                {
                                    type: 'CLSS',
                                    property: 'class_code',
                                    label: 'Class Code',
                                    ui: 'checklist',
                                    enableSearch: true,
                                },
                                {
                                    type: 'DOCUMENT_META',
                                    property: 'document_type',
                                    label: 'Document Type',
                                    ui: 'checklist',
                                    enableSearch: true,
                                    allowedValues: ['Factsheet', 'KIID'],
                                },
                                {
                                    type: 'DOCUMENT_PROPERTY',
                                    property: 'cultureCode',
                                    label: 'Document Language',
                                    ui: 'checklist',
                                    enableSearch: true,
                                    allowedValues: [
                                        'en-GB',
                                        'fr-FR',
                                        'it-IT',
                                        'de-DE',
                                    ],
                                },
                            ],
                            documentSearch: [
                                {
                                    property: 'document_type',
                                    values: ['Factsheet', 'KIID'],
                                    matchtype: 'MATCH',
                                },
                            ],
                            cultureAliases: {
                                'en-GB': 'English',
                                'fr-FR': 'French',
                                'de-DE': 'German',
                                'it-IT': 'Italian',
                            },
                            disclaimerTypes: ['Fees', 'Past Perf'],
                        } as ModuleConfig,
                    },
                    {
                        id: 'performance',
                        label: 'Bond Performance',
                        selected: true,
                        config: {
                            fundGroupProperty: 'fund_name',
                            documentGroupMeta: 'document_type',
                            watchListEnabled: true,
                            watchListProperty: 'isin',
                            defaultCulture: 'en-GB',
                            identifierSearch: {
                                properties: ['isin', 'fund_name'],
                                fuzzy: false,
                            },
                            searchLimit: 30,
                            autoSearchMinChar: 3,
                            sort: [
                                {
                                    key: 'fund_name',
                                    direction: 'ASC',
                                },
                                {
                                    key: 'isin',
                                    direction: 'ASC',
                                },
                            ],
                            documentsJoinSpec: [
                                {
                                    joinTo: 'isin',
                                    joinFrom: 'isin',
                                },
                                {
                                    joinTo: 'fund_name',
                                    joinFrom: 'fund_name',
                                },
                            ],
                            columnConfig: [
                                {
                                    property: 'isin',
                                    label: 'ISIN',
                                },
                                {
                                    property: 'class_code',
                                    label: 'Class Code',
                                },
                                {
                                    property: 'ccy',
                                    label: 'Currency',
                                },
                                {
                                    property: 'distribution_type',
                                    label: 'Distribution Type',
                                },
                            ],
                            rowConfig: {
                                type: 'FundCard',
                                header: {
                                    nameProperty: 'public_share_class_name',
                                    linkProperty: 'long_name',
                                    linkLabel: 'Fund Page',
                                },
                                fundCommentary: {
                                    label: 'Investment Objective',
                                    commentaries: [
                                        {
                                            commentaryType: 'Fund Commentary',
                                        },
                                    ],
                                },
                                fundFacts: {
                                    label: 'Fund Facts',
                                    noDataHidden: true,
                                    cards: [
                                        {
                                            label: 'Price (NAV)',
                                            primary: true,
                                            data: {
                                                property: 'timeseries',
                                                code: 'prices',
                                                index: -1,
                                                key: 'value',
                                                format: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    },
                                                },
                                            },
                                            asAtDate: {
                                                label: 'As at',
                                                data: {
                                                    property: 'timeseries',
                                                    code: 'returns',
                                                    ccy: 'NA',
                                                    key: 'date',
                                                    index: 0,
                                                    format: {
                                                        type: 'date',
                                                        options: {
                                                            year: '2-digit',
                                                            month: 'short',
                                                            day: '2-digit',
                                                        },
                                                    },
                                                },
                                            },
                                            disclaimer: {
                                                disclaimerType: 'KIID Charges',
                                                linked: true,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'Bid Price',
                                            primary: true,
                                            data: {
                                                property: 'properties_pub',
                                                code: 'bid_price',
                                                noDataPlaceholder: 'n/a',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: true,
                                            },
                                            asAtDate: {
                                                label: 'As at',
                                                data: {
                                                    property: 'properties_pub',
                                                    code: 'fund_launch_date',
                                                    key: 'value',
                                                },
                                            },
                                        },
                                        {
                                            label: 'Offer Price',
                                            primary: true,
                                            data: {
                                                property: 'properties_pub',
                                                code: 'offer_price',
                                                noDataPlaceholder: 'n/a',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'Price Change',
                                            primary: true,
                                            data: [
                                                {
                                                    property: 'timeseries',
                                                    code: 'prices',
                                                    index: 0,
                                                    key: 'value',
                                                    format: {
                                                        type: 'number',
                                                        options: {
                                                            style: 'decimal',
                                                            minimumFractionDigits: 1,
                                                            maximumFractionDigits: 1,
                                                        },
                                                    },
                                                    separator: ' / ',
                                                },
                                                {
                                                    property: 'timeseries',
                                                    code: 'prices',
                                                    index: -1,
                                                    key: 'value',
                                                    format: {
                                                        type: 'number',
                                                        options: {
                                                            style: 'decimal',
                                                            minimumFractionDigits: 1,
                                                            maximumFractionDigits: 1,
                                                        },
                                                    },
                                                    showChange: true,
                                                },
                                            ],
                                        },
                                        {
                                            label: 'SRRI',
                                            higherRiskLabel: 'High',
                                            lowerRiskLabel: 'Low',
                                            primary: true,
                                            ui: 'srri',
                                            data: {
                                                property: 'statistics',
                                                code: 'srri',
                                                index: 0,
                                                key: 'value',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'KIID Risks',
                                                linked: true,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'Morningstar Rating',
                                            primary: true,
                                            ui: 'stars',
                                            data: {
                                                property: 'statistics',
                                                code: 'srri',
                                                index: 0,
                                                key: 'value',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'KIID Risks',
                                                linked: true,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'AUM',
                                            primary: true,
                                            property: 'aum',
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: false,
                                            },
                                        },
                                        {
                                            label: 'Ongoing Charge',
                                            primary: true,
                                            data: {
                                                property: 'properties_pub',
                                                code: 'management_charge',
                                                key: 'value',
                                                format: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    },
                                                },
                                            },
                                        },
                                        {
                                            label: 'Yield',
                                            property: 'yield',
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'SRRI',
                                            ui: 'srri',
                                            data: {
                                                property: 'statistics',
                                                code: 'srri',
                                                index: 0,
                                                key: 'value',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'KIID Risks',
                                                linked: true,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'AUM',
                                            property: 'aum',
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: false,
                                            },
                                            asAtDate: {
                                                label: 'As at',
                                                data: {
                                                    property: 'properties_pub',
                                                    code: 'fund_launch_date',
                                                    key: 'value',
                                                },
                                            },
                                        },
                                        {
                                            label: 'Ongoing Charge',
                                            data: {
                                                property: 'properties_pub',
                                                code: 'management_charge',
                                                key: 'value',
                                                format: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    },
                                                },
                                            },
                                        },
                                        {
                                            label: 'Pay Dates',
                                            data: [
                                                {
                                                    property: 'properties_pub',
                                                    code: 'inception_date',
                                                    key: 'value',
                                                    separator: ', ',
                                                },
                                                {
                                                    property: 'properties_pub',
                                                    code: 'launch_date',
                                                    key: 'value',
                                                    separator: ', ',
                                                },
                                                {
                                                    property: 'properties_pub',
                                                    code: 'inception_date',
                                                    key: 'value',
                                                    separator: ', ',
                                                },
                                                {
                                                    property: 'properties_pub',
                                                    code: 'launch_date',
                                                    key: 'value',
                                                },
                                            ],
                                        },
                                    ],
                                    disclaimers: [
                                        {
                                            disclaimerType: 'Fees',
                                            linked: false,
                                        },
                                        {
                                            disclaimerType: 'KIID Charges',
                                            linked: true,
                                        },
                                    ],
                                },
                                fundManagers: {
                                    label: 'Fund Managers',
                                    showImage: true,
                                    showBiography: true,
                                    showExtended: [
                                        {
                                            property: 'title',
                                        },
                                    ],
                                    link: {
                                        label: 'Read Bio',
                                        property: 'bio_link',
                                    },
                                    autoPlay: 3000,
                                },
                                charts: [
                                    {
                                        id: 'cumulativePerformance',
                                        label: 'Cumulative Performance',
                                        selected: false,
                                        grouped: true,
                                        type: 'LineChart',
                                        data: {
                                            property: 'timeseries',
                                            code: 'prices',
                                            ccy: 'NA',
                                            classification: 'NA',
                                            periodicity: 'MONTHLY',
                                        },
                                        config: {
                                            legend: true,
                                            grid: {
                                                stroke: 'gray300',
                                                vertical: false,
                                            },
                                            tooltip: {
                                                labelFormat: {
                                                    type: 'date',
                                                    options: {
                                                        year: '2-digit',
                                                        month: 'short',
                                                    },
                                                },
                                                valueFormat: {
                                                    type: 'currency',
                                                    options: {
                                                        style: 'currency',
                                                        currency: 'GBP',
                                                        currencyDisplay: 'code',
                                                        minimumFractionDigits: 1,
                                                        maximumFractionDigits: 2,
                                                    },
                                                },
                                            },
                                            xAxis: {
                                                dataKey: 'date',
                                                format: {
                                                    type: 'date',
                                                    options: {
                                                        year: '2-digit',
                                                        month: 'short',
                                                    },
                                                },
                                            },
                                            yAxis: {
                                                label: {
                                                    value: 'Price',
                                                    angle: -90,
                                                    position: 'insideLeft',
                                                },
                                                dataKey: 'value',
                                            },
                                            series: [
                                                {
                                                    label: 'Fund',
                                                    dataKey: 'value',
                                                    color: 'primary',
                                                },
                                            ],
                                        },
                                        asAtDate: {
                                            label: 'As at',
                                            data: {
                                                property: 'timeseries',
                                                code: 'returns',
                                                ccy: 'NA',
                                                key: 'date',
                                                index: 0,
                                            },
                                            format: {
                                                type: 'date',
                                                options: {
                                                    year: '2-digit',
                                                    month: 'short',
                                                    day: '2-digit',
                                                },
                                            },
                                        },
                                        noDataDisclaimerTypes: [
                                            {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                            },
                                        ],
                                        disclaimerTypes: [
                                            {
                                                disclaimerType: 'KIID Charges',
                                                linked: true,
                                            },
                                        ],
                                    },
                                    {
                                        id: 'annualisedPerformance',
                                        label: 'Annualised Performance',
                                        selected: true,
                                        grouped: true,
                                        type: 'BarChart',
                                        data: {
                                            property: 'statistics',
                                            code: 'performance_annual_returns',
                                            ccy: 'NA',
                                        },
                                        config: {
                                            legend: true,
                                            grid: false,
                                            tooltip: {
                                                valueFormat: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 1,
                                                    },
                                                },
                                            },
                                            xAxis: {
                                                dataKey: 'label',
                                                type: 'category',
                                            },
                                            yAxis: {
                                                label: {
                                                    value: 'Percentage %',
                                                    angle: -90,
                                                    position: 'insideLeft',
                                                },
                                                type: 'number',
                                            },
                                            series: [
                                                {
                                                    name: 'Fund',
                                                    nameProperty: 'fund_name',
                                                    dataKey: 'value',
                                                    fill: 'primary',
                                                    unit: '%',
                                                },
                                                {
                                                    name: 'Benchmark',
                                                    dataKey: 'value',
                                                    fill: 'gray500',
                                                    unit: '%',
                                                },
                                            ],
                                        },
                                        noDataDisclaimerTypes: [
                                            {
                                                disclaimerType: 'KIID Charges',
                                                linked: true,
                                            },
                                            {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                            },
                                        ],
                                        disclaimerTypes: [
                                            {
                                                disclaimerType:
                                                    'KIID Past Perf',
                                                linked: true,
                                            },
                                            {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                            },
                                        ],
                                    },
                                    {
                                        id: 'cumulativePerformance2',
                                        label: 'Cumulative Performance 2',
                                        selected: false,
                                        grouped: false,
                                        type: 'LineChart',
                                        data: {
                                            property: 'timeseries',
                                            code: 'returns',
                                            ccy: 'NA',
                                            classification: 'NA',
                                            periodicity: 'MONTHLY',
                                        },
                                        config: {
                                            legend: true,
                                            grid: {
                                                vertical: false,
                                            },
                                            tooltip: {
                                                labelFormat: {
                                                    type: 'date',
                                                    options: {
                                                        day: '2-digit',
                                                        month: 'long',
                                                        year: 'numeric',
                                                    },
                                                },
                                                valueFormat: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 3,
                                                    },
                                                },
                                            },
                                            xAxis: {
                                                dataKey: 'date',
                                                format: {
                                                    type: 'date',
                                                    options: {
                                                        year: '2-digit',
                                                        month: 'short',
                                                    },
                                                },
                                            },
                                            yAxis: {
                                                format: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 3,
                                                    },
                                                },
                                            },
                                            series: [
                                                {
                                                    dataKey: 'benchmarkReturn',
                                                    color: 'primary',
                                                },
                                                {
                                                    dataKey: 'benchmarkReturn2',
                                                    color: 'secondary',
                                                    connectNulls: true,
                                                },
                                            ],
                                        },
                                        noDataDisclaimerTypes: [
                                            'No Chart Data',
                                        ],
                                        disclaimerTypes: ['Past Perf'],
                                    },
                                ],
                                keyDocuments: {
                                    label: 'Key Documents',
                                    documentTypes: ['Factsheet', 'XXX', 'KIID'],
                                    linkProperty: 'long_name',
                                    linkLabel: 'View all documents',
                                },
                                footnoteScheme: 'numeric',
                                disclaimers: [
                                    {
                                        disclaimerType:
                                            'KIID Practical Information',
                                        linked: true,
                                    },
                                ],
                            },
                            clearIndividualFilters: {
                                enabled: true,
                                align: 'right',
                            },
                            filters: [
                                {
                                    type: 'CLSS',
                                    property: 'fund_name',
                                    label: 'Fund Name',
                                    ui: 'checklist',
                                    enableSearch: true,
                                },
                                {
                                    type: 'CLSS',
                                    property: 'isin',
                                    label: 'ISIN',
                                    ui: 'checklist',
                                    enableSearch: true,
                                },
                                {
                                    type: 'CLSS',
                                    property: 'ccy',
                                    label: 'Currency',
                                    ui: 'checklist',
                                    enableSearch: true,
                                },
                                {
                                    type: 'CLSS',
                                    property: 'distribution_type',
                                    label: 'Distribution Type',
                                    ui: 'checklist',
                                    enableSearch: true,
                                },
                                {
                                    type: 'CLSS',
                                    property: 'class_code',
                                    label: 'Class Code',
                                    ui: 'checklist',
                                    enableSearch: true,
                                },
                                {
                                    type: 'DOCUMENT_META',
                                    property: 'document_type',
                                    label: 'Document Type',
                                    ui: 'checklist',
                                    enableSearch: true,
                                    allowedValues: ['Factsheet'],
                                },
                                {
                                    type: 'DOCUMENT_PROPERTY',
                                    property: 'cultureCode',
                                    label: 'Document Language',
                                    ui: 'checklist',
                                    enableSearch: true,
                                    allowedValues: [
                                        'en-GB',
                                        'fr-FR',
                                        'it-IT',
                                        'de-DE',
                                    ],
                                },
                            ],
                            documentSearch: [
                                {
                                    property: 'document_type',
                                    values: ['Factsheet', 'KIID'],
                                    matchtype: 'MATCH',
                                },
                            ],
                            cultureAliases: {
                                'en-GB': 'English',
                                'fr-FR': 'French',
                                'de-DE': 'German',
                                'it-IT': 'Italian',
                            },
                            disclaimerTypes: ['Fees', 'Past Perf'],
                        } as ModuleConfig,
                    },
                    {
                        id: 'overview',
                        label: 'Bond Overview',
                        config: {
                            fundGroupProperty: 'isin',
                            documentGroupMeta: 'document_type',
                            enableWatchlist: true,
                            defaultCulture: 'en-GB',
                            identifierSearch: {
                                properties: ['isin', 'fund_name'],
                                fuzzy: false,
                            },
                            searchLimit: 30,
                            autoSearchMinChar: 3,
                            sort: [
                                {
                                    key: 'fund_name',
                                    direction: 'ASC',
                                },
                                {
                                    key: 'isin',
                                    direction: 'ASC',
                                },
                            ],
                            documentsJoinSpec: [
                                {
                                    joinTo: 'isin',
                                    joinFrom: 'isin',
                                },
                                {
                                    joinTo: 'fund_name',
                                    joinFrom: 'fund_name',
                                },
                            ],
                            columnConfig: [
                                {
                                    property: 'isin',
                                    label: 'ISIN',
                                },
                                {
                                    property: 'sedol',
                                    label: 'SEDOL',
                                },
                                {
                                    property: 'class_code',
                                    label: 'Class Code',
                                },
                            ],
                            rowConfig: {
                                type: 'FundCard',
                                header: {
                                    nameProperty: 'public_share_class_name',
                                    linkProperty: 'long_name',
                                    linkLabel: 'Fund Page',
                                },
                                fundCommentary: {
                                    label: 'Investment Objective',
                                    commentaries: [
                                        {
                                            commentaryType: 'Fund Commentary',
                                        },
                                    ],
                                },
                                fundFacts: {
                                    label: 'Fund Facts',
                                    cards: [
                                        {
                                            label: 'Price (NAV)',
                                            primary: true,
                                            data: {
                                                property: 'timeseries',
                                                code: 'prices',
                                                index: -1,
                                                key: 'value',
                                                format: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    },
                                                },
                                            },
                                            asAtDate: {
                                                label: 'As at',
                                                data: {
                                                    property: 'timeseries',
                                                    code: 'returns',
                                                    ccy: 'NA',
                                                    key: 'date',
                                                    index: 0,
                                                    format: {
                                                        type: 'date',
                                                        options: {
                                                            year: '2-digit',
                                                            month: 'short',
                                                            day: '2-digit',
                                                        },
                                                    },
                                                },
                                            },
                                            disclaimer: {
                                                disclaimerType: 'KIID Charges',
                                                linked: true,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'Bid Price',
                                            primary: true,
                                            data: {
                                                property: 'properties_pub',
                                                code: 'bid_price',
                                                noDataPlaceholder: 'n/a',
                                                noDataHidden: true,
                                            },
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: true,
                                            },
                                            asAtDate: {
                                                label: 'As at',
                                                data: {
                                                    property: 'properties_pub',
                                                    code: 'fund_launch_date',
                                                    key: 'value',
                                                },
                                            },
                                        },
                                        {
                                            label: 'Offer Price',
                                            primary: true,
                                            data: {
                                                property: 'properties_pub',
                                                code: 'offer_price',
                                                noDataPlaceholder: 'n/a',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'Price Change',
                                            primary: true,
                                            data: [
                                                {
                                                    property: 'timeseries',
                                                    code: 'prices',
                                                    index: 0,
                                                    key: 'value',
                                                    format: {
                                                        type: 'number',
                                                        options: {
                                                            style: 'decimal',
                                                            minimumFractionDigits: 1,
                                                            maximumFractionDigits: 1,
                                                        },
                                                    },
                                                    separator: ' / ',
                                                },
                                                {
                                                    property: 'timeseries',
                                                    code: 'prices',
                                                    index: -1,
                                                    key: 'value',
                                                    format: {
                                                        type: 'number',
                                                        options: {
                                                            style: 'decimal',
                                                            minimumFractionDigits: 1,
                                                            maximumFractionDigits: 1,
                                                        },
                                                    },
                                                    showChange: true,
                                                },
                                            ],
                                        },
                                        {
                                            label: 'SRRI',
                                            higherRiskLabel: 'High',
                                            lowerRiskLabel: 'Low',
                                            primary: true,
                                            ui: 'srri',
                                            data: {
                                                property: 'statistics',
                                                code: 'srri',
                                                index: 0,
                                                key: 'value',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'KIID Risks',
                                                linked: true,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'Morningstar Rating',
                                            primary: true,
                                            ui: 'stars',
                                            data: {
                                                property: 'statistics',
                                                code: 'srri',
                                                index: 0,
                                                key: 'value',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'KIID Risks',
                                                linked: true,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'AUM',
                                            primary: true,
                                            property: 'aum',
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: false,
                                            },
                                        },
                                        {
                                            label: 'Ongoing Charge',
                                            primary: true,
                                            data: {
                                                property: 'properties_pub',
                                                code: 'management_charge',
                                                key: 'value',
                                                format: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    },
                                                },
                                            },
                                        },
                                        {
                                            label: 'Yield',
                                            property: 'yield',
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'SRRI',
                                            ui: 'srri',
                                            data: {
                                                property: 'statistics',
                                                code: 'srri',
                                                index: 0,
                                                key: 'value',
                                            },
                                            disclaimer: {
                                                disclaimerType: 'KIID Risks',
                                                linked: true,
                                                footnote: true,
                                            },
                                        },
                                        {
                                            label: 'AUM',
                                            property: 'aum',
                                            disclaimer: {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                                footnote: false,
                                            },
                                            asAtDate: {
                                                label: 'As at',
                                                data: {
                                                    property: 'properties_pub',
                                                    code: 'fund_launch_date',
                                                    key: 'value',
                                                },
                                            },
                                        },
                                        {
                                            label: 'Ongoing Charge',
                                            data: {
                                                property: 'properties_pub',
                                                code: 'management_charge',
                                                key: 'value',
                                                format: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    },
                                                },
                                            },
                                        },
                                        {
                                            label: 'Pay Dates',
                                            data: [
                                                {
                                                    property: 'properties_pub',
                                                    code: 'inception_date',
                                                    key: 'value',
                                                    separator: ', ',
                                                },
                                                {
                                                    property: 'properties_pub',
                                                    code: 'launch_date',
                                                    key: 'value',
                                                    separator: ', ',
                                                },
                                                {
                                                    property: 'properties_pub',
                                                    code: 'inception_date',
                                                    key: 'value',
                                                    separator: ', ',
                                                },
                                                {
                                                    property: 'properties_pub',
                                                    code: 'launch_date',
                                                    key: 'value',
                                                },
                                            ],
                                        },
                                    ],
                                    disclaimers: [
                                        {
                                            disclaimerType: 'Fees',
                                            linked: false,
                                        },
                                        {
                                            disclaimerType: 'KIID Charges',
                                            linked: true,
                                        },
                                    ],
                                },
                                fundManagers: {
                                    label: 'Fund Managers',
                                    showImage: true,
                                    showBiography: true,
                                    showExtended: [
                                        {
                                            property: 'title',
                                        },
                                    ],
                                    link: {
                                        label: 'Read Bio',
                                        property: 'bio_link',
                                    },
                                    autoPlay: 3000,
                                },
                                charts: [
                                    {
                                        id: 'cumulativePerformance',
                                        label: 'Cumulative Performance',
                                        selected: false,
                                        grouped: true,
                                        type: 'LineChart',
                                        data: {
                                            property: 'timeseries',
                                            code: 'prices',
                                            ccy: 'NA',
                                            classification: 'NA',
                                            periodicity: 'MONTHLY',
                                        },
                                        config: {
                                            legend: true,
                                            grid: {
                                                stroke: 'gray300',
                                                vertical: false,
                                            },
                                            tooltip: {
                                                labelFormat: {
                                                    type: 'date',
                                                    options: {
                                                        year: '2-digit',
                                                        month: 'short',
                                                    },
                                                },
                                                valueFormat: {
                                                    type: 'currency',
                                                    options: {
                                                        style: 'currency',
                                                        currency: 'GBP',
                                                        currencyDisplay: 'code',
                                                        minimumFractionDigits: 1,
                                                        maximumFractionDigits: 2,
                                                    },
                                                },
                                            },
                                            xAxis: {
                                                dataKey: 'date',
                                                format: {
                                                    type: 'date',
                                                    options: {
                                                        year: '2-digit',
                                                        month: 'short',
                                                    },
                                                },
                                            },
                                            yAxis: {
                                                label: {
                                                    value: 'Price',
                                                    angle: -90,
                                                    position: 'insideLeft',
                                                },
                                                dataKey: 'value',
                                            },
                                            series: [
                                                {
                                                    label: 'Fund',
                                                    dataKey: 'value',
                                                    color: 'primary',
                                                },
                                            ],
                                        },
                                        asAtDate: {
                                            label: 'As at',
                                            data: {
                                                property: 'timeseries',
                                                code: 'returns',
                                                ccy: 'NA',
                                                key: 'date',
                                                index: 0,
                                            },
                                            format: {
                                                type: 'date',
                                                options: {
                                                    year: '2-digit',
                                                    month: 'short',
                                                    day: '2-digit',
                                                },
                                            },
                                        },
                                        noDataDisclaimerTypes: [
                                            {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                            },
                                        ],
                                        disclaimerTypes: [
                                            {
                                                disclaimerType: 'KIID Charges',
                                                linked: true,
                                            },
                                        ],
                                    },
                                    {
                                        id: 'annualisedPerformance',
                                        label: 'Annualised Performance',
                                        selected: true,
                                        grouped: true,
                                        type: 'BarChart',
                                        data: {
                                            property: 'statistics',
                                            code: 'performance_annual_returns',
                                            ccy: 'NA',
                                        },
                                        config: {
                                            legend: true,
                                            grid: false,
                                            tooltip: {
                                                valueFormat: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 1,
                                                    },
                                                },
                                            },
                                            xAxis: {
                                                dataKey: 'label',
                                                type: 'category',
                                            },
                                            yAxis: {
                                                label: {
                                                    value: 'Percentage %',
                                                    angle: -90,
                                                    position: 'insideLeft',
                                                },
                                                type: 'number',
                                            },
                                            series: [
                                                {
                                                    name: 'Fund',
                                                    nameProperty: 'fund_name',
                                                    dataKey: 'value',
                                                    fill: 'primary',
                                                    unit: '%',
                                                },
                                                {
                                                    name: 'Benchmark',
                                                    dataKey: 'value',
                                                    fill: 'gray500',
                                                    unit: '%',
                                                },
                                            ],
                                        },
                                        noDataDisclaimerTypes: [
                                            {
                                                disclaimerType: 'KIID Charges',
                                                linked: true,
                                            },
                                            {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                            },
                                        ],
                                        disclaimerTypes: [
                                            {
                                                disclaimerType:
                                                    'KIID Past Perf',
                                                linked: true,
                                            },
                                            {
                                                disclaimerType: 'Fees',
                                                linked: false,
                                            },
                                        ],
                                    },
                                    {
                                        id: 'cumulativePerformance2',
                                        label: 'Cumulative Performance 2',
                                        selected: false,
                                        grouped: false,
                                        type: 'LineChart',
                                        data: {
                                            property: 'timeseries',
                                            code: 'returns',
                                            ccy: 'NA',
                                            classification: 'NA',
                                            periodicity: 'MONTHLY',
                                        },
                                        config: {
                                            legend: true,
                                            grid: {
                                                vertical: false,
                                            },
                                            tooltip: {
                                                labelFormat: {
                                                    type: 'date',
                                                    options: {
                                                        day: '2-digit',
                                                        month: 'long',
                                                        year: 'numeric',
                                                    },
                                                },
                                                valueFormat: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 3,
                                                    },
                                                },
                                            },
                                            xAxis: {
                                                dataKey: 'date',
                                                format: {
                                                    type: 'date',
                                                    options: {
                                                        year: '2-digit',
                                                        month: 'short',
                                                    },
                                                },
                                            },
                                            yAxis: {
                                                format: {
                                                    type: 'number',
                                                    options: {
                                                        style: 'decimal',
                                                        minimumFractionDigits: 3,
                                                    },
                                                },
                                            },
                                            series: [
                                                {
                                                    dataKey: 'benchmarkReturn',
                                                    color: 'primary',
                                                },
                                                {
                                                    dataKey: 'benchmarkReturn2',
                                                    color: 'secondary',
                                                    connectNulls: true,
                                                },
                                            ],
                                        },
                                        noDataDisclaimerTypes: [
                                            'No Chart Data',
                                        ],
                                        disclaimerTypes: ['Past Perf'],
                                    },
                                ],
                                keyDocuments: {
                                    label: 'Key Documents',
                                    documentTypes: ['Factsheet', 'XXX', 'KIID'],
                                    linkProperty: 'long_name',
                                    linkLabel: 'View all documents',
                                },
                                footnoteScheme: 'numeric',
                                disclaimers: [
                                    {
                                        disclaimerType:
                                            'KIID Practical Information',
                                        linked: true,
                                    },
                                ],
                            },
                            clearIndividualFilters: {
                                enabled: true,
                                align: 'right',
                            },
                            filters: [
                                {
                                    type: 'CLSS',
                                    property: 'fund_name',
                                    label: 'Fund Name',
                                    ui: 'checklist',
                                    enableSearch: true,
                                },
                                {
                                    type: 'CLSS',
                                    property: 'ccy',
                                    label: 'Currency',
                                    ui: 'checklist',
                                    enableSearch: true,
                                },
                                {
                                    type: 'DOCUMENT_META',
                                    property: 'document_type',
                                    label: 'Document Type',
                                    ui: 'checklist',
                                    enableSearch: true,
                                    allowedValues: ['KIID', 'Factsheet'],
                                },
                                {
                                    type: 'DOCUMENT_PROPERTY',
                                    property: 'cultureCode',
                                    label: 'Document Language',
                                    ui: 'checklist',
                                    enableSearch: true,
                                    allowedValues: [
                                        'en-GB',
                                        'fr-FR',
                                        'it-IT',
                                        'de-DE',
                                    ],
                                },
                            ],
                            documentSearch: [
                                {
                                    property: 'document_type',
                                    values: ['Factsheet', 'KIID'],
                                    matchtype: 'MATCH',
                                },
                            ],
                            cultureAliases: {
                                'en-GB': 'English',
                                'fr-FR': 'French',
                                'de-DE': 'German',
                                'it-IT': 'Italian',
                            },
                            disclaimerTypes: ['Fees', 'Past Perf'],
                        } as ModuleConfig,
                    },
                    {
                        id: 'library',
                        label: 'Bond Library',
                        config: {
                            documentGroupMeta: 'document_type',
                            documentGroupOrder: ['KIID', 'Factsheet'],
                            watchListEnabled: true,
                            watchListProperty: 'isin',
                            defaultCulture: 'en-GB',
                            selectedDocumentsLabel: {
                                singular: `{selected}/{total} Document selected`,
                                plural: `{selected}/{total} Documents selected`,
                            },
                            availableDocumentsLabel: {
                                singular: `{total} Document available`,
                                plural: `{total} Documents available`,
                            },
                            identifierSearch: {
                                properties: ['isin', 'fund_name'],
                                fuzzy: false,
                            },
                            searchLimit: 30,
                            autoSearchMinChar: 3,
                            sort: [
                                {
                                    key: 'fund_name',
                                    direction: 'ASC',
                                },
                                {
                                    key: 'isin',
                                    direction: 'ASC',
                                },
                            ],
                            documentsJoinSpec: [
                                {
                                    joinTo: 'isin',
                                    joinFrom: 'isin',
                                },
                                {
                                    joinTo: 'fund_name',
                                    joinFrom: 'fund_name',
                                },
                            ],
                            columnConfig: [
                                {
                                    property: 'isin',
                                    label: 'ISIN',
                                },
                                {
                                    property: 'class_code',
                                    label: 'Class Code',
                                },
                                {
                                    property: 'ccy',
                                    label: 'Currency',
                                },
                                {
                                    property: 'distribution_type',
                                    label: 'Distribution Type',
                                },
                            ],
                            rowConfig: {
                                type: 'Documents',
                                // documentGroups: [
                                //     {
                                //         heading: {
                                //             queryOptionsType: 'none',
                                //             value: 'Legal Documents',
                                //         },
                                //         groupBy: {
                                //             queryOptionsType: 'documents',
                                //             entityType: 'CLSS',
                                //             collectionItemConditional: {
                                //                 conditions: [
                                //                     {
                                //                         field: 'inferred.Category',
                                //                         operator: 'includes',
                                //                         value: 'Legal',
                                //                     },
                                //                 ],
                                //             },
                                //         },
                                //     },
                                //     {
                                //         heading: {
                                //             queryOptionsType: 'none',
                                //             value: 'General Documents',
                                //         },
                                //         groupBy: {
                                //             queryOptionsType: 'documents',
                                //             entityType: 'CLSS',
                                //             collectionItemConditional: {
                                //                 conditions: [
                                //                     {
                                //                         field: 'inferred.Category',
                                //                         operator: 'includes',
                                //                         value: 'General',
                                //                     },
                                //                 ],
                                //             },
                                //         },
                                //     },
                                // ],
                            },
                            clearIndividualFilters: {
                                enabled: true,
                                align: 'right',
                            },
                            filters: [
                                {
                                    type: 'CLSS',
                                    property: 'fund_name',
                                    label: 'Fund Name',
                                    ui: 'checklist',
                                    enableSearch: true,
                                },
                                {
                                    type: 'CLSS',
                                    property: 'isin',
                                    label: 'ISIN',
                                    ui: 'checklist',
                                    enableSearch: true,
                                },
                                {
                                    type: 'CLSS',
                                    property: 'ccy',
                                    label: 'Currency',
                                    ui: 'checklist',
                                    enableSearch: true,
                                },
                                {
                                    type: 'CLSS',
                                    property: 'distribution_type',
                                    label: 'Distribution Type',
                                    ui: 'checklist',
                                    enableSearch: true,
                                },
                                {
                                    type: 'CLSS',
                                    property: 'class_code',
                                    label: 'Class Code',
                                    ui: 'checklist',
                                    enableSearch: true,
                                },
                                {
                                    type: 'DOCUMENT_META',
                                    property: 'document_type',
                                    label: 'Document Type',
                                    ui: 'checklist',
                                    enableSearch: true,
                                    allowedValues: ['KIID', 'Factsheet'],
                                },
                                {
                                    type: 'DOCUMENT_PROPERTY',
                                    property: 'cultureCode',
                                    label: 'Document Language',
                                    ui: 'checklist',
                                    enableSearch: true,
                                    allowedValues: [
                                        'en-GB',
                                        'fr-FR',
                                        'it-IT',
                                        'de-DE',
                                    ],
                                },
                            ],
                            documentSearchBySegmentation: {
                                'UK Individual': [
                                    {
                                        property: 'document_type',
                                        values: ['KIID'],
                                        matchtype: 'MATCH',
                                    },
                                ],
                            },
                            cultureAliases: {
                                'en-GB': 'English',
                                'fr-FR': 'French',
                                'de-DE': 'German',
                                'it-IT': 'Italian',
                            },
                            disclaimerTypes: ['Fees', 'Past Perf'],
                        } as ModuleConfig,
                    },
                ],
            },
        ],
    },
};



// WEBPACK FOOTER //
// ./src/development.appConfig.ts